// src/pages/portfolio/FYP.js
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
    Activity, Bell, MapPin, Lightbulb, ShieldCheck, User, Edit, 
    Save, Search, X, Trash2, LogOut, Play, Square, 
    Loader2, ChevronDown, ChevronUp, TrendingUp, BrainCircuit, Gauge, 
    Mail, Phone, Map, Server, BookOpen, UserCheck, Users, Download, Eye, Wifi, Thermometer, Filter, Palette, ActivitySquare, Lock, ArrowRight, Radio, Cpu, Database
} from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, YAxis, Tooltip, XAxis, CartesianGrid } from 'recharts';

import { db } from '../../firebase'; 
import { ref, onValue, push, remove, update, onDisconnect } from 'firebase/database';
import { generateTriageSummary, generateDeepAnalysis } from '../../aiService'; 

import lockBg from '../../assets/lock_bg.png'; 
import dashboardBg from '../../assets/dashboard_bg.png'; 

// --- 1. MODULAR COMPONENTS ---

const BpmGauge = React.memo(({ bpm, isCalculating, t }) => {
    const maxBpm = 60; 
    const clampedBpm = Math.min(Math.max(bpm, 0), maxBpm);
    const finalRotation = (clampedBpm / maxBpm) * 180 - 90;

    let statusColor = t.chartLineNormal; 
    if (bpm > 0 && (bpm < 12 || bpm > 25)) statusColor = t.chartLineAbnormal; 
    if (bpm === 0) statusColor = "#6b7280"; 

    return (
        <div className={`flex flex-col items-center justify-center p-4 ${t.innerBg} rounded-2xl border ${t.borderColor} relative shadow-inner transition-colors duration-500`}>
            <h4 className={`${t.subText} text-[10px] font-bold uppercase tracking-widest mb-4 flex items-center gap-2`}>
                <Gauge size={14} /> Live Respiratory Rate
            </h4>
            
            <div className="relative w-48 h-24 overflow-hidden">
                <div className={`absolute bottom-0 left-0 w-48 h-48 border-[16px] ${t.borderColor} rounded-full box-border border-b-transparent border-r-transparent transform -rotate-45 transition-colors duration-500`}></div>
                <div className="absolute bottom-0 left-0 w-48 h-48 border-[16px] border-green-500/20 rounded-full box-border border-b-transparent border-r-transparent transform -rotate-45" style={{ clipPath: 'polygon(20% 0, 80% 0, 50% 50%)' }}></div>
                
                <span className={`absolute bottom-2 left-4 text-[10px] ${t.subText} font-bold`}>0</span>
                <span className={`absolute top-2 left-1/2 -translate-x-1/2 text-[10px] ${t.subText} font-bold`}>30</span>
                <span className={`absolute bottom-2 right-4 text-[10px] ${t.subText} font-bold`}>60</span>

                <motion.div 
                    className={`absolute bottom-0 left-1/2 w-1 h-20 ${t.mainText} origin-bottom rounded-full shadow-lg bg-current`}
                    style={{ marginLeft: '-2px' }}
                    initial={{ rotate: -90 }}
                    animate={ isCalculating ? { rotate: [-60, 60, -60] } : { rotate: finalRotation } }
                    transition={ isCalculating ? { repeat: Infinity, duration: 2.5, ease: "easeInOut" } : { type: "spring", stiffness: 50, damping: 12 } }
                />
                <div className="absolute bottom-[-8px] left-1/2 w-4 h-4 bg-gray-400 rounded-full shadow-md" style={{ marginLeft: '-8px' }}></div>
            </div>
            
            <div className="mt-4 text-center h-12 flex flex-col justify-center">
                {isCalculating ? (
                    <span className={`${t.textPrimary} text-xs font-bold animate-pulse tracking-widest`}>CALCULATING...</span>
                ) : (
                    <>
                        <span className="text-4xl font-black tracking-widest" style={{ color: statusColor }}>
                            {bpm > 0 ? bpm : '--'}
                        </span>
                        <span className={`${t.subText} text-sm ml-2 font-bold`}>BPM</span>
                    </>
                )}
            </div>
        </div>
    );
});

const LockScreen = React.memo(({ doctorPin, setDoctorPin, handleDoctorLogin, patientLoginId, setPatientLoginId, handlePatientLogin, loginError, t }) => (
    <div className={`min-h-screen ${t.appBg} flex items-center justify-center p-4 md:p-6 font-mono relative transition-colors duration-500`}>
        <div className="absolute inset-0 z-0"><img src={lockBg} alt="Security" className={`w-full h-full object-cover transition-opacity duration-500 ${t.imgOpacity}`} /></div>
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`max-w-md w-full ${t.cardBg} border ${t.borderPrimary} p-8 md:p-10 rounded-3xl relative z-10 text-center shadow-2xl`}>
            <h2 className={`text-2xl md:text-3xl font-black ${t.mainText} tracking-[0.2em] mb-6`}>SYSTEM LOGIN</h2>
            {loginError && <div className="text-red-500 font-bold mb-4 animate-pulse">{loginError}</div>}
            <div className={`${t.innerBg} p-6 rounded-2xl border ${t.borderColor} mb-4 transition-colors duration-500`}>
                <p className={`${t.textPrimary} text-sm font-bold mb-3 flex items-center justify-center gap-2`}><ShieldCheck size={18}/> Doctor Portal</p>
                <form onSubmit={handleDoctorLogin}>
                    <input type="password" value={doctorPin} onChange={(e) => setDoctorPin(e.target.value)} placeholder="ENTER PIN" className={`w-full ${t.appBg} border-2 ${t.borderColor} text-center text-xl tracking-[0.5em] py-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} />
                </form>
            </div>
            <div className={`${t.innerBg} p-6 rounded-2xl border ${t.borderColor} transition-colors duration-500`}>
                <p className={`${t.textSecondary} text-sm font-bold mb-3 flex items-center justify-center gap-2`}><User size={18}/> Patient Portal</p>
                <form onSubmit={handlePatientLogin}>
                    <input type="text" value={patientLoginId} onChange={(e) => setPatientLoginId(e.target.value)} placeholder="ENTER PATIENT ID" className={`w-full ${t.appBg} border-2 ${t.borderColor} text-center text-sm py-3 rounded-xl ${t.mainText} outline-none mb-3 focus:${t.borderSecondary} uppercase transition-colors`} />
                    <button type="submit" className={`w-full ${t.bgSecondary}/20 ${t.textSecondary} border ${t.borderSecondary}/50 hover:${t.bgSecondary} hover:text-white py-3 rounded-xl font-bold transition-colors`}>Access Records</button>
                </form>
            </div>
        </motion.div>
    </div>
));

const DocsTab = React.memo(({ t }) => (
    <div className={`flex-1 flex flex-col items-center justify-center p-6 md:p-10 ${t.cardBg} border ${t.borderColor} rounded-2xl mt-4 min-h-[60vh] shadow-lg transition-colors duration-500`}>
        <BookOpen size={64} className={`${t.textPrimary} opacity-50 mb-6`} />
        <h2 className={`text-2xl md:text-3xl font-black ${t.mainText} mb-2 text-center`}>Project Documentation</h2>
        <p className={`${t.subText} mb-8 max-w-md text-center text-sm md:text-base`}>Repository for research papers, user manuals, and presentation materials for RF Sensing.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl">
            <div className={`p-8 border ${t.borderColor} rounded-xl ${t.innerBg} hover:${t.borderPrimary} transition flex flex-col group shadow-lg`}>
                <h3 className={`text-lg font-bold ${t.mainText} group-hover:${t.textPrimary} transition`}>My Thesis</h3>
                <p className={`text-sm ${t.subText} mt-2 flex-1`}>Comprehensive guide to LabVIEW and Firebase integrations.</p>
                <div className={`flex justify-between items-center mt-6 border-t ${t.borderColor} pt-4`}>
                    <p className={`text-xs ${t.subText}`}>PDF Document</p>
                    <div className="flex gap-2">
                        <a href="/docs/thesis.pdf" target="_blank" rel="noreferrer" className={`p-2 ${t.cardBg} border ${t.borderColor} hover:${t.bgPrimary} hover:text-white rounded transition ${t.subText}`}><Eye size={16}/></a>
                        <a href="/docs/thesis.pdf" download className={`p-2 ${t.cardBg} border ${t.borderColor} hover:${t.bgPrimary} hover:text-white rounded transition ${t.subText}`}><Download size={16}/></a>
                    </div>
                </div>
            </div>
            <div className={`p-8 border ${t.borderColor} rounded-xl ${t.innerBg} hover:${t.borderPrimary} transition flex flex-col group shadow-lg`}>
                <h3 className={`text-lg font-bold ${t.mainText} group-hover:${t.textPrimary} transition`}>Clinical Evaluation Protocol</h3>
                <p className={`text-sm ${t.subText} mt-2 flex-1`}>Study procedures and validation metrics for RF sensing vs standard contact sensors.</p>
                <div className={`flex justify-between items-center mt-6 border-t ${t.borderColor} pt-4`}>
                    <p className={`text-xs ${t.subText}`}>PDF Document</p>
                    <div className="flex gap-2">
                        <a href="/docs/clinical_protocol.pdf" target="_blank" rel="noreferrer" className={`p-2 ${t.cardBg} border ${t.borderColor} hover:${t.bgPrimary} hover:text-white rounded transition ${t.subText}`}><Eye size={16}/></a>
                        <a href="/docs/clinical_protocol.pdf" download className={`p-2 ${t.cardBg} border ${t.borderColor} hover:${t.bgPrimary} hover:text-white rounded transition ${t.subText}`}><Download size={16}/></a>
                    </div>
                </div>
            </div>
        </div>
    </div>
));

const DevicesTab = React.memo(({ hardwareMetrics, alarmActive, gsmActive, lightActive, t }) => {
    const getActuatorStatus = () => {
        let statuses = [];
        if(alarmActive) statuses.push("Alarm (ON)");
        if(gsmActive) statuses.push("GPS/Call (ON)");
        if(lightActive) statuses.push("Lights (ON)");
        return statuses.length > 0 ? statuses.join(" | ") : "All Systems Normal / Off";
    };

    return (
        <div className={`flex-1 flex flex-col items-center justify-center p-6 md:p-10 ${t.cardBg} border ${t.borderColor} rounded-2xl mt-4 min-h-[60vh] shadow-lg transition-colors duration-500`}>
            <Server size={64} className={`${t.textPrimary} opacity-50 mb-6`} />
            <h2 className={`text-2xl md:text-3xl font-black ${t.mainText} mb-2 text-center`}>Device & Hardware Node Status</h2>
            <p className={`${t.subText} mb-8 max-w-md text-center text-sm md:text-base`}>Monitor the live connection state of the physical hardware modules.</p>
            
            <div className={`w-full max-w-5xl border ${t.borderColor} rounded-xl overflow-x-auto shadow-2xl`}>
                <table className="w-full text-left text-sm whitespace-nowrap md:whitespace-normal">
                    <thead className={`${t.innerBg} ${t.subText}`}>
                        <tr>
                            <th className="p-5 font-bold">Node Name</th>
                            <th className="p-5 font-bold">Hardware Type</th>
                            <th className="p-5 font-bold">Metrics</th>
                            <th className="p-5 font-bold">Status</th>
                            <th className="p-5 font-bold text-right">Uptime</th>
                        </tr>
                    </thead>
                    <tbody className={`divide-y ${t.borderColor} ${t.appBg} text-base transition-colors duration-500`}>
                        <tr>
                            <td className={`p-5 font-bold ${t.mainText}`}>USRP Streamer</td>
                            <td className={`p-5 ${t.subText}`}>LabVIEW Processing Node</td>
                            <td className={`p-5 text-xs ${t.subText} flex flex-col gap-1`}>
                                <span className="flex items-center gap-2"><Wifi size={12} className={t.textPrimary}/> Signal: {hardwareMetrics.usrp.signal} dBm</span>
                                <span className="flex items-center gap-2"><Activity size={12} className={t.textPrimary}/> Latency: {hardwareMetrics.usrp.latency}ms</span>
                            </td>
                            <td className="p-5"><span className="text-green-500 flex items-center gap-2 font-bold"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> {hardwareMetrics.usrp.status}</span></td>
                            <td className={`p-5 text-right ${t.subText}`}>{hardwareMetrics.usrp.uptime}</td>
                        </tr>
                        <tr>
                            <td className={`p-5 font-bold ${t.mainText}`}>IoT Gateway</td>
                            <td className={`p-5 ${t.subText}`}>Raspberry Pi 5</td>
                            <td className={`p-5 text-xs ${t.subText} flex flex-col gap-1`}>
                                <span className="flex items-center gap-2"><Thermometer size={12} className={hardwareMetrics.rpi.temp > 70 ? "text-red-500" : "text-yellow-500"}/> CPU Temp: {hardwareMetrics.rpi.temp}°C</span>
                                <span className="flex items-center gap-2"><Activity size={12} className="text-yellow-500"/> Latency: {hardwareMetrics.rpi.latency}ms</span>
                            </td>
                            <td className="p-5"><span className="text-green-500 flex items-center gap-2 font-bold"><div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div> {hardwareMetrics.rpi.status}</span></td>
                            <td className={`p-5 text-right ${t.subText}`}>{hardwareMetrics.rpi.uptime}</td>
                        </tr>
                        <tr>
                            <td className={`p-5 font-bold ${t.mainText}`}>Physical Actuators</td>
                            <td className={`p-5 ${t.subText}`}>Room Relays (Global)</td>
                            <td className={`p-5 text-xs font-bold ${t.textPrimary}`}>{getActuatorStatus()}</td>
                            <td className="p-5">
                                {(alarmActive || gsmActive || lightActive) ? (
                                    <span className="text-red-500 flex items-center gap-2 font-bold"><div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div> Active</span>
                                ) : (
                                    <span className="text-yellow-500 flex items-center gap-2 font-bold"><div className="w-2 h-2 rounded-full bg-yellow-500"></div> Standby</span>
                                )}
                            </td>
                            <td className={`p-5 text-right ${t.subText}`}>--</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    );
});


// --- 2. MAIN APP COMPONENT ---
// ✅ ENHANCED: Public Landing Page for Recruiters & Visitors
// ✅ Public Landing Page for Recruiters & Visitors
// Grounded in the FYP thesis:
// "RF Sensing for Respiratory Complications using IoT for Smart Cities"

const FYPLandingPage = React.memo(({ onProceed, t }) => (
    <div className={`min-h-screen ${t.appBg} flex flex-col font-sans relative overflow-x-hidden transition-colors duration-500`}>
        {/* Background */}
        <div className="absolute inset-0 z-0">
            <img
                src={dashboardBg}
                alt="Background"
                className={`w-full h-full object-cover transition-opacity duration-500 ${t.imgOpacity}`}
            />
        </div>

        {/* =========================================================
            1. HERO
        ========================================================= */}
        <div className="w-full py-24 px-6 relative z-10 flex flex-col items-center justify-center text-center mt-8">

            <div className="w-24 h-24 rounded-3xl bg-teal-500/10 border border-teal-500/30 flex items-center justify-center mb-6 shadow-[0_0_40px_rgba(20,184,166,0.2)] backdrop-blur-md">
                <Radio size={48} className="text-teal-400" />
            </div>

            <span className="text-teal-500 font-bold tracking-[0.3em] uppercase text-xs mb-4 block">
                Final Year Project • COMSATS University Islamabad
            </span>

            <h1 className={`text-4xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r ${t.gradient} mb-6 tracking-tight max-w-5xl`}>
                RF Sensing for Respiratory Complications using IoT for Smart Cities
            </h1>

            <p className={`${t.subText} max-w-4xl text-lg leading-relaxed mb-10`}>
                A contactless respiratory monitoring system that combines Software Defined
                Radio, signal processing, edge machine learning, Firebase, React, and
                Agentic AI to detect and monitor six distinct respiratory patterns and
                support real-time remote healthcare surveillance.
            </p>

            <button
                onClick={onProceed}
                className={`px-8 py-4 rounded-xl font-bold text-white shadow-lg transition-all hover:scale-105 active:scale-95 flex items-center gap-3 ${t.bgPrimary} hover:opacity-90`}
            >
                Access Clinical Dashboard
                <ArrowRight size={20} />
            </button>
        </div>


        {/* =========================================================
            2. PROJECT PROBLEM
        ========================================================= */}
        <div className="max-w-5xl mx-auto text-center px-8 relative z-10 mb-24">

            <ShieldCheck size={40} className="text-teal-500 mx-auto mb-6" />

            <h2 className={`text-2xl md:text-3xl font-bold ${t.mainText} mb-5`}>
                The Problem
            </h2>

            <p className={`${t.subText} text-sm md:text-base leading-relaxed`}>
                Conventional respiratory monitoring commonly depends on contact-based
                sensors, wearables, or manual observation. These approaches can introduce
                discomfort, compliance challenges, and infection-related concerns during
                long-term monitoring. This project explores a contactless alternative
                capable of monitoring respiratory activity without attaching a sensor to
                the patient.
            </p>
        </div>


        {/* =========================================================
            3. CONTACTLESS RF SENSING
        ========================================================= */}
        <div className="max-w-7xl mx-auto w-full px-6 relative z-10 mb-24">

            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-12 text-center`}>
                Contactless RF Sensing
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* RF */}
                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <Wifi size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            RF Sensing
                        </h3>
                    </div>

                    <p className={`${t.subText} text-sm leading-relaxed`}>
                        The system uses Software Defined Radio in a bistatic
                        configuration to transmit and receive RF signals. A 910 MHz
                        OFDM signal with 256 subcarriers is used to capture changes
                        produced by respiratory chest movement.
                    </p>
                </div>


                {/* CSI */}
                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <ActivitySquare size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            CSI & DSP
                        </h3>
                    </div>

                    <p className={`${t.subText} text-sm leading-relaxed`}>
                        Channel State Information is extracted from the received
                        signal. A 3–4 Hz low-pass denoise filter is applied to isolate
                        the respiratory waveform from environmental and high-frequency
                        interference.
                    </p>
                </div>


                {/* Waveform */}
                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>
                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <Radio size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            Respiratory Waveform
                        </h3>
                    </div>

                    <p className={`${t.subText} text-sm leading-relaxed`}>
                        The processed RF signal represents respiratory movement and
                        becomes the input for feature extraction and machine learning
                        classification.
                    </p>
                </div>

            </div>
        </div>


        {/* =========================================================
            4. THREE-LAYER SYSTEM ARCHITECTURE
        ========================================================= */}
        <div className="max-w-7xl mx-auto w-full px-6 relative z-10 mb-24">

            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-12 text-center`}>
                Edge-to-Cloud System Architecture
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                {/* Layer 1 */}
                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl hover:border-teal-500/50 transition-colors backdrop-blur-md flex flex-col`}>

                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <Wifi size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            1. Hardware Sensing
                        </h3>
                    </div>

                    <ul className={`${t.subText} text-sm space-y-3 leading-relaxed flex-grow list-disc pl-5`}>
                        <li>
                            Bistatic Software Defined Radio configuration.
                        </li>

                        <li>
                            910 MHz OFDM transmission with 256 subcarriers.
                        </li>

                        <li>
                            NI USRP hardware performs RF transmission and reception.
                        </li>

                        <li>
                            LabVIEW controls the TX/RX signal processing pipeline.
                        </li>

                        <li>
                            Channel State Information captures respiratory-related
                            phase and amplitude variations.
                        </li>
                    </ul>
                </div>


                {/* Layer 2 */}
                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl hover:border-teal-500/50 transition-colors backdrop-blur-md flex flex-col`}>

                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <Cpu size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            2. Edge Computing
                        </h3>
                    </div>

                    <ul className={`${t.subText} text-sm space-y-3 leading-relaxed flex-grow list-disc pl-5`}>
                        <li>
                            LabVIEW streams processed respiratory telemetry to the
                            Raspberry Pi through TCP/IP.
                        </li>

                        <li>
                            The Raspberry Pi maintains a continuous 10-second rolling
                            window.
                        </li>

                        <li>
                            28 features are extracted from each window: 14 time-domain
                            and 14 frequency-domain features.
                        </li>

                        <li>
                            The Random Forest classifier performs real-time respiratory
                            classification.
                        </li>

                        <li>
                            Local GPIO hardware provides immediate physical alerts.
                        </li>
                    </ul>
                </div>


                {/* Layer 3 */}
                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl hover:border-teal-500/50 transition-colors backdrop-blur-md flex flex-col`}>

                    <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <BrainCircuit size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            3. Cloud AI & IoT
                        </h3>
                    </div>

                    <ul className={`${t.subText} text-sm space-y-3 leading-relaxed flex-grow list-disc pl-5`}>
                        <li>
                            Firebase Realtime Database synchronizes respiratory
                            telemetry and patient history.
                        </li>

                        <li>
                            React provides the clinical monitoring dashboard.
                        </li>

                        <li>
                            Doctor and Patient portals provide role-specific access.
                        </li>

                        <li>
                            An integrated LLM generates context-aware historical
                            summaries.
                        </li>

                        <li>
                            Agentic operation can interact with physical IoT hardware
                            through structured decisions.
                        </li>
                    </ul>
                </div>

            </div>
        </div>


        {/* =========================================================
            5. EDGE AI PIPELINE
        ========================================================= */}
        <div className="max-w-5xl mx-auto w-full px-8 relative z-10 mb-24">

            <div className={`${t.innerBg} rounded-3xl border ${t.borderColor} p-8 md:p-10`}>

                <div className="text-center mb-10">
                    <h2 className={`text-2xl md:text-3xl font-bold ${t.mainText} mb-4`}>
                        Edge Machine Learning
                    </h2>

                    <p className={`${t.subText} text-sm leading-relaxed max-w-3xl mx-auto`}>
                        The RF respiratory waveform is processed on the Raspberry Pi 5, where 28 extracted features are passed to a Random Forest classifier for real-time respiratory pattern classification.
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">

                    <div className="text-center">
                        <span className={`block text-3xl font-black ${t.textPrimary} mb-2`}>
                            10s
                        </span>

                        <span className={`text-[10px] uppercase font-bold tracking-widest ${t.subText}`}>
                            Rolling Window
                        </span>
                    </div>

                    <div className="text-center">
                        <span className={`block text-3xl font-black ${t.textPrimary} mb-2`}>
                            28
                        </span>

                        <span className={`text-[10px] uppercase font-bold tracking-widest ${t.subText}`}>
                            Extracted Features
                        </span>
                    </div>

                    <div className="text-center">
                        <span className={`block text-3xl font-black ${t.textPrimary} mb-2`}>
                            6
                        </span>

                        <span className={`text-[10px] uppercase font-bold tracking-widest ${t.subText}`}>
                            Respiratory Classes
                        </span>
                    </div>

                    <div className="text-center">
                        <span className={`block text-3xl font-black ${t.textPrimary} mb-2`}>
                            99.64%
                        </span>

                        <span className={`text-[10px] uppercase font-bold tracking-widest ${t.subText}`}>
                            Reported RF Accuracy
                        </span>
                    </div>

                </div>
            </div>
        </div>


        {/* =========================================================
            6. SIX RESPIRATORY CLASSES
        ========================================================= */}
        <div className="max-w-6xl mx-auto w-full px-6 relative z-10 mb-24">

            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-12 text-center`}>
                Respiratory Classification
            </h2>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">

                <div className={`${t.cardBg} p-6 rounded-2xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`font-bold ${t.mainText} mb-2`}>
                        Eupnea
                    </h3>

                    <p className={`${t.subText} text-xs`}>
                        Normal breathing
                    </p>
                </div>

                <div className={`${t.cardBg} p-6 rounded-2xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`font-bold ${t.mainText} mb-2`}>
                        Bradypnea
                    </h3>

                    <p className={`${t.subText} text-xs`}>
                        Slow breathing
                    </p>
                </div>

                <div className={`${t.cardBg} p-6 rounded-2xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`font-bold ${t.mainText} mb-2`}>
                        Tachypnea
                    </h3>

                    <p className={`${t.subText} text-xs`}>
                        Rapid breathing
                    </p>
                </div>

                <div className={`${t.cardBg} p-6 rounded-2xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`font-bold ${t.mainText} mb-2`}>
                        Biot
                    </h3>

                    <p className={`${t.subText} text-xs`}>
                        Irregular breathing with apnea periods
                    </p>
                </div>

                <div className={`${t.cardBg} p-6 rounded-2xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`font-bold ${t.mainText} mb-2`}>
                        Sighing
                    </h3>

                    <p className={`${t.subText} text-xs`}>
                        Normal breathing with deep sighs
                    </p>
                </div>

                <div className={`${t.cardBg} p-6 rounded-2xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`font-bold ${t.mainText} mb-2`}>
                        Kussmaul
                    </h3>

                    <p className={`${t.subText} text-xs`}>
                        Deep and rapid breathing
                    </p>
                </div>

            </div>
        </div>
        {/* AI & AGENTIC IoT CONTROL */}
<section className={`py-24 ${dashboardBg}`}>
    <div className="max-w-7xl mx-auto px-6">

        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-400/20 bg-cyan-400/5 text-cyan-300 text-sm mb-6">
                <BrainCircuit className="w-4 h-4" />
                AI & Agentic IoT Layer
            </div>

            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">
                From Respiratory Sensing
                <span className="text-cyan-400"> to Intelligent Action</span>
            </h2>

            <p className="text-slate-400 text-lg leading-relaxed">
                The system does more than classify respiratory patterns.
                AI connects real-time respiratory telemetry with clinical
                summaries, human oversight, and — in autonomous operation —
                physical IoT actions.
            </p>
        </div>

        {/* AI Flow */}
        <div className="mb-16">
            <div className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 md:p-8">

                <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-0">

                    <div className="w-full md:flex-1 text-center p-4">
                        <Radio className="w-7 h-7 text-cyan-400 mx-auto mb-3" />
                        <div className="text-white font-semibold">
                            RF Sensing
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Respiratory telemetry
                        </div>
                    </div>

                    <ArrowRight className="hidden md:block w-5 h-5 text-slate-600" />

                    <div className="w-full md:flex-1 text-center p-4">
                        <Cpu className="w-7 h-7 text-cyan-400 mx-auto mb-3" />
                        <div className="text-white font-semibold">
                            Edge ML
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Respiratory classification
                        </div>
                    </div>

                    <ArrowRight className="hidden md:block w-5 h-5 text-slate-600" />

                    <div className="w-full md:flex-1 text-center p-4">
                        <Wifi className="w-7 h-7 text-cyan-400 mx-auto mb-3" />
                        <div className="text-white font-semibold">
                            Live Telemetry
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Cloud & IoT data
                        </div>
                    </div>

                    <ArrowRight className="hidden md:block w-5 h-5 text-slate-600" />

                    <div className="w-full md:flex-1 text-center p-4">
                        <BrainCircuit className="w-7 h-7 text-cyan-400 mx-auto mb-3" />
                        <div className="text-white font-semibold">
                            LLM Analysis
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Clinical interpretation
                        </div>
                    </div>

                    <ArrowRight className="hidden md:block w-5 h-5 text-slate-600" />

                    <div className="w-full md:flex-1 text-center p-4">
                        <ActivitySquare className="w-7 h-7 text-cyan-400 mx-auto mb-3" />
                        <div className="text-white font-semibold">
                            IoT Action
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                            Physical response
                        </div>
                    </div>

                </div>
            </div>
        </div>

        {/* Three AI Modes */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Manual Spot Mode */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7 hover:border-cyan-400/30 transition-colors">

                <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-cyan-400/10 flex items-center justify-center">
                        <ShieldCheck className="w-5 h-5 text-cyan-400" />
                    </div>

                    <div>
                        <h3 className="text-xl font-bold text-white">
                            Manual Spot Mode
                        </h3>
                        <p className="text-xs text-slate-500">
                            Human initiated AI analysis
                        </p>
                    </div>
                </div>

                <p className="text-slate-400 leading-relaxed mb-5">
                    A doctor can manually initiate an AI evaluation of the
                    current respiratory data window. The AI analyzes the
                    available telemetry and generates a clinical summary
                    for review.
                </p>

                <div className="text-sm text-cyan-300">
                    Doctor → AI Analysis → Clinical Summary
                </div>
            </div>

            {/* Discrete AI Mode */}
            <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-7 hover:border-cyan-400/30 transition-colors">

                <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-cyan-400/10 flex items-center justify-center">
                        <BrainCircuit className="w-5 h-5 text-cyan-400" />
                    </div>

                    <div>
                        <h3 className="text-xl font-bold text-white">
                            Discrete AI Mode
                        </h3>
                        <p className="text-xs text-slate-500">
                            Human-in-the-loop monitoring
                        </p>
                    </div>
                </div>

                <p className="text-slate-400 leading-relaxed mb-5">
                    The AI continuously monitors incoming telemetry and
                    provides clinical summaries while keeping physical
                    IoT control under human supervision.
                </p>

                <div className="text-sm text-cyan-300">
                    Live Data → AI Monitoring → Human Oversight
                </div>
            </div>

            {/* Autonomous LLM Mode */}
            <div className="rounded-2xl border border-cyan-400/20 bg-cyan-400/5 p-7 hover:border-cyan-400/40 transition-colors">

                <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-cyan-400/10 flex items-center justify-center">
                        <Cpu className="w-5 h-5 text-cyan-400" />
                    </div>

                    <div>
                        <h3 className="text-xl font-bold text-white">
                            Autonomous LLM Mode
                        </h3>
                        <p className="text-xs text-cyan-300">
                            Agentic IoT operation
                        </p>
                    </div>
                </div>

                <p className="text-slate-300 leading-relaxed mb-5">
                    The LLM analyzes real-time telemetry and can produce
                    a structured action command. The application processes
                    this command and can update the IoT control state,
                    allowing the AI layer to participate in physical
                    intervention.
                </p>

                <div className="text-sm text-cyan-300">
                    Telemetry → LLM Decision → Structured Command → IoT Action
                </div>
            </div>

        </div>

        {/* Human-in-the-Loop */}
        <div className="mt-10 rounded-2xl border border-slate-800 bg-slate-900/40 p-7 md:p-9">

            <div className="flex flex-col md:flex-row md:items-center gap-6">

                <div className="w-14 h-14 shrink-0 rounded-2xl bg-cyan-400/10 flex items-center justify-center">
                    <ShieldCheck className="w-7 h-7 text-cyan-400" />
                </div>

                <div>
                    <h3 className="text-xl font-bold text-white mb-2">
                        Human-in-the-Loop Clinical Control
                    </h3>

                    <p className="text-slate-400 leading-relaxed">
                        The AI layer is designed to work alongside clinical
                        users. Depending on the selected operating mode,
                        the system can provide analysis and summaries for
                        human review or participate in the IoT control loop
                        through structured commands.
                    </p>
                </div>

            </div>
        </div>

        {/* Physical Action */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">

            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-5">
                <div className="text-cyan-400 text-sm font-semibold mb-2">
                    AI ANALYSIS
                </div>
                <div className="text-white font-medium">
                    Respiratory telemetry
                </div>
                <div className="text-slate-500 text-sm mt-1">
                    Real-time physiological data
                </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-5">
                <div className="text-cyan-400 text-sm font-semibold mb-2">
                    DECISION
                </div>
                <div className="text-white font-medium">
                    Structured AI command
                </div>
                <div className="text-slate-500 text-sm mt-1">
                    Application-controlled action state
                </div>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-950/50 p-5">
                <div className="text-cyan-400 text-sm font-semibold mb-2">
                    PHYSICAL RESPONSE
                </div>
                <div className="text-white font-medium">
                    IoT intervention
                </div>
                <div className="text-slate-500 text-sm mt-1">
                    Connected hardware response
                </div>
            </div>

        </div>

    </div>
</section>


        {/* =========================================================
            7. PHYSICAL IoT ECOSYSTEM
        ========================================================= */}
        <div className="max-w-7xl mx-auto w-full px-6 relative z-10 mb-24">

            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-12 text-center`}>
                Physical IoT Intervention
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>
                    <div className="flex items-center gap-4 mb-5">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <ActivitySquare size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            GPIO Alerts
                        </h3>
                    </div>

                    <p className={`${t.subText} text-sm leading-relaxed`}>
                        The Raspberry Pi controls a visual LED and an active buzzer
                        through GPIO outputs for local respiratory alerts.
                    </p>
                </div>


                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>
                    <div className="flex items-center gap-4 mb-5">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <Radio size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            GPS Tracking
                        </h3>
                    </div>

                    <p className={`${t.subText} text-sm leading-relaxed`}>
                        A Ublox NEO-6M GPS module provides location telemetry through
                        UART communication with the Raspberry Pi.
                    </p>
                </div>


                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>
                    <div className="flex items-center gap-4 mb-5">
                        <div className="p-3 bg-teal-500/10 rounded-xl border border-teal-500/20">
                            <BrainCircuit size={24} className="text-teal-400" />
                        </div>

                        <h3 className={`text-xl font-bold ${t.mainText}`}>
                            WhatsApp Notification
                        </h3>
                    </div>

                    <p className={`${t.subText} text-sm leading-relaxed`}>
                        Green API is used to send the respiratory state together with
                        a Google Maps location link through WhatsApp.
                    </p>
                </div>

            </div>
        </div>


        {/* =========================================================
            8. CLINICAL DASHBOARD
        ========================================================= */}
        <div className="max-w-7xl mx-auto w-full px-6 relative z-10 mb-24">

            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-12 text-center`}>
                Clinical Monitoring Dashboard
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>

                    <h3 className={`text-xl font-bold ${t.mainText} mb-5`}>
                        Doctor Portal
                    </h3>

                    <ul className={`${t.subText} text-sm space-y-3 leading-relaxed list-disc pl-5`}>
                        <li>
                            Patient registration and patient selection.
                        </li>

                        <li>
                            Live respiratory waveform and BPM display.
                        </li>

                        <li>
                            Historical respiratory classification data.
                        </li>

                        <li>
                            AI operational mode selection.
                        </li>

                        <li>
                            Context-aware LLM summaries.
                        </li>

                        <li>
                            Manual and automated IoT control.
                        </li>
                    </ul>
                </div>


                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} shadow-2xl backdrop-blur-md`}>

                    <h3 className={`text-xl font-bold ${t.mainText} mb-5`}>
                        Patient Portal
                    </h3>

                    <ul className={`${t.subText} text-sm space-y-3 leading-relaxed list-disc pl-5`}>
                        <li>
                            Restricted access using the assigned Patient ID.
                        </li>

                        <li>
                            Live respiratory monitoring.
                        </li>

                        <li>
                            Current BPM and breathing graph.
                        </li>

                        <li>
                            Patient-specific respiratory information.
                        </li>

                        <li>
                            Advanced IoT actuator controls remain restricted.
                        </li>
                    </ul>
                </div>

            </div>
        </div>


        
        {/* =========================================================
            10. VALIDATED PROTOTYPE
        ========================================================= */}
        <div className="max-w-5xl mx-auto text-center px-8 relative z-10 mb-24">

            <h2 className={`text-2xl md:text-3xl font-bold ${t.mainText} mb-5`}>
                Physical Prototype
            </h2>

            <p className={`${t.subText} text-sm leading-relaxed max-w-4xl mx-auto`}>
                The prototype connects the USRP sensing hardware, Raspberry Pi edge
                processor, physical alert hardware, GPS module, Firebase backend,
                and React dashboard into a complete end-to-end monitoring ecosystem.
                A two-room setup was also used to demonstrate remote monitoring
                between a patient ward and a remote nursing station.
            </p>
        </div>


        {/* =========================================================
            11. PROJECT SCOPE
        ========================================================= */}
        <div className="max-w-5xl mx-auto w-full px-8 relative z-10 mb-24">

            <div className={`${t.cardBg} rounded-3xl border ${t.borderColor} p-8 md:p-10`}>

                <h2 className={`text-2xl font-bold ${t.mainText} text-center mb-6`}>
                    Current Research Scope
                </h2>

                <p className={`${t.subText} text-sm leading-relaxed text-center`}>
                    The implemented prototype focuses on respiratory monitoring of a
                    single stationary subject in a controlled indoor environment.
                    The system demonstrates the complete sensing, classification,
                    cloud synchronization, visualization, and IoT intervention pipeline
                    while providing a foundation for future expansion.
                </p>

            </div>
        </div>


        {/* =========================================================
            12. SDG IMPACT
        ========================================================= */}
        <div className="max-w-5xl mx-auto w-full px-8 relative z-10 mb-24">

            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-10 text-center`}>
                Broader Impact
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`text-xl font-bold ${t.mainText} mb-3`}>
                        SDG 3
                    </h3>

                    <p className={`${t.subText} text-sm`}>
                        Good Health and Well-being
                    </p>
                </div>

                <div className={`${t.cardBg} p-8 rounded-3xl border ${t.borderColor} text-center backdrop-blur-md`}>
                    <h3 className={`text-xl font-bold ${t.mainText} mb-3`}>
                        SDG 9
                    </h3>

                    <p className={`${t.subText} text-sm`}>
                        Industry, Innovation and Infrastructure
                    </p>
                </div>

            </div>
        </div>


        {/* =========================================================
            13. FINAL CTA
        ========================================================= */}
        <div className="max-w-4xl mx-auto text-center px-8 relative z-10 pb-28">

            <div className={`${t.innerBg} border ${t.borderColor} rounded-3xl p-10`}>

                <Radio size={36} className="text-teal-400 mx-auto mb-5" />

                <h2 className={`text-2xl md:text-3xl font-bold ${t.mainText} mb-4`}>
                    Explore the Monitoring System
                </h2>

                <p className={`${t.subText} text-sm leading-relaxed mb-8`}>
                    Explore the implemented dashboard and the complete edge-to-cloud
                    respiratory monitoring workflow.
                </p>

                <button
                    onClick={onProceed}
                    className={`px-8 py-4 rounded-xl font-bold text-white shadow-lg transition-all hover:scale-105 active:scale-95 inline-flex items-center gap-3 ${t.bgPrimary} hover:opacity-90`}
                >
                    Enter Clinical Dashboard
                    <ArrowRight size={20} />
                </button>

            </div>
        </div>

    </div>
));

const FYP = () => {
    const navigate = useNavigate();

    // STATE MANAGEMENT
    const [showLandingPage, setShowLandingPage] = useState(true);
    const [isLocked, setIsLocked] = useState(true);
    const [showDoctorInitialSelect, setShowDoctorInitialSelect] = useState(false);
    const [doctorPin, setDoctorPin] = useState('');
    const [loginError, setLoginError] = useState('');
    const [userRole, setUserRole] = useState(null); 
    const [patientLoginId, setPatientLoginId] = useState('');
    
    // Theme Management
    const [isClinicalTheme, setIsClinicalTheme] = useState(false);
    
    // Unified Monitoring Mode State: 'manual' | 'agentic_discrete' | 'agentic_continuous'
    const [monitoringMode, setMonitoringMode] = useState('manual');
    const [anomalySensitivity, setAnomalySensitivity] = useState(3); 
    
    const [autoSaveCountdown, setAutoSaveCountdown] = useState(null);

    // --- NEW: THEFT MODAL STATE ---
    const [sessionInterrupted, setSessionInterrupted] = useState(false);

    // --- HARDWARE LOCK STATE ---
    const [hardwareLock, setHardwareLock] = useState({ is_locked: false, host_id: '', patient_id: '', patient_name: '' });
    const hostId = useMemo(() => 'host_' + Math.random().toString(36).substring(2, 9), []);

    // UI Navigation
    const [activeTab, setActiveTab] = useState('dashboard'); 

    // Telemetry & Systems
    const [alarmActive, setAlarmActive] = useState(false);
    const [gsmActive, setGsmActive] = useState(false);
    const [lightActive, setLightActive] = useState(false);
    
    const controlsStateRef = useRef({ alarm: false, gsm: false, light: false });

    const [history, setHistory] = useState([]);
    
    const [sessionActive, setSessionActive] = useState(false); 
    const [currentSessionChunks, setCurrentSessionChunks] = useState([]);
    const [currentBpm, setCurrentBpm] = useState(0);

    const [aiAcuteSummary, setAiAcuteSummary] = useState('Standby. Start a session to generate AI insights.');
    const [aiDeepAnalysis, setAiDeepAnalysis] = useState('');
    const [isAiLoading, setIsAiLoading] = useState(false);

    const [hardwareMetrics, setHardwareMetrics] = useState({
        usrp: { signal: -45, latency: 12, status: 'Online', uptime: '2h 15m' },
        rpi: { temp: 52, latency: 34, status: 'Online', uptime: '5d 12h' }
    });

    const [showProfileModal, setShowProfileModal] = useState(false);
    const [viewingPatientId, setViewingPatientId] = useState(''); 
    const [quickSearchTerm, setQuickSearchTerm] = useState(''); 
    const [dbSearchTerm, setDbSearchTerm] = useState(''); 
    
    const [dbGlobalDateFilter, setDbGlobalDateFilter] = useState('all');
    const [dbShowOnlyAI, setDbShowOnlyAI] = useState(false);
    const [dbPatientFilters, setDbPatientFilters] = useState({});

    const [patient, setPatient] = useState({
        id: 'P-F-0001', 
        name: 'Fahad', fatherName: 'Ali',
        phone: '+92 300 1234567', email: 'fahad@example.com',
        address: 'Sector H-12', city: 'Islamabad', country: 'Pakistan',
        date: new Date().toLocaleDateString(), classification: 'System Ready'
    });

    const [editPatientForm, setEditPatientForm] = useState({...patient});
    const [graphFilter, setGraphFilter] = useState('all'); 
    const [expandedGroups, setExpandedGroups] = useState({}); 

    // --- REFS FOR PERFORMANCE & CLOSURE SAFETY ---
    const sessionActiveRef = useRef(sessionActive);
    const monitoringModeRef = useRef(monitoringMode);
    const triggerCooldownRef = useRef(0);
    const lastProcessedTimestampRef = useRef(null);

    // --- NEW: RACE CONDITION FIX REF ---
    const intentionalStopRef = useRef(false);

    // NEW REFS for safely saving interrupted data
    const currentSessionChunksRef = useRef([]);
    const patientRef = useRef(patient);

    useEffect(() => { sessionActiveRef.current = sessionActive; }, [sessionActive]);
    useEffect(() => { monitoringModeRef.current = monitoringMode; }, [monitoringMode]);
    useEffect(() => { currentSessionChunksRef.current = currentSessionChunks; }, [currentSessionChunks]);
    useEffect(() => { patientRef.current = patient; }, [patient]);

    useEffect(() => {
        triggerCooldownRef.current = 0;
    }, [monitoringMode]);

    useEffect(() => {
        controlsStateRef.current = { alarm: alarmActive, gsm: gsmActive, light: lightActive };
    }, [alarmActive, gsmActive, lightActive]);

    const playAlertSound = useCallback(() => {
        try {
            const audio = new Audio('https://actions.google.com/sounds/v1/alarms/beep_short.ogg');
            audio.play();
        } catch (e) {
            console.error("Audio play failed. Browser may block auto-play.", e);
        }
    }, []);

    // --- AGENTIC COUNTDOWN EFFECT ---
    useEffect(() => {
        if (autoSaveCountdown !== null && autoSaveCountdown > 0) {
            const timer = setTimeout(() => setAutoSaveCountdown(autoSaveCountdown - 1), 1000);
            return () => clearTimeout(timer);
        } else if (autoSaveCountdown === 0) {
            const isContinuousSave = monitoringModeRef.current === 'agentic_continuous';
            saveSessionSummary(isContinuousSave ? 'continuous' : 'discrete_agentic');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoSaveCountdown]);

    // --- LOGIC & HELPERS ---
    
    const finalizeBPM = useCallback((chunks) => {
        if (chunks.length === 0) return 0;
        
        const latestChunk = chunks[chunks.length - 1];
        if (!latestChunk.waveData || latestChunk.waveData.length < 3) return 0;
        
        const wave = latestChunk.waveData;
        const mean = wave.reduce((a, b) => a + b, 0) / wave.length;
        
        let totalPeaks = 0;
        let isAbove = wave[0] > mean;
        
        for (let i = 1; i < wave.length; i++) {
            if (wave[i] > mean && !isAbove) {
                totalPeaks++;
                isAbove = true;
            } else if (wave[i] < mean && isAbove) {
                isAbove = false;
            }
        }
        return Math.round((totalPeaks / 10) * 60);
    }, []);

    const severityWeight = useMemo(() => ({ 'Kussmaul': 6, 'Biot': 5, 'Fast Tachypnea': 4, 'Slow Bradypnea': 3, 'Sighing': 2, 'Normal Eupnea': 1 }), []);
    
    const calculateAggregatedResult = useCallback((arr) => {
        if(arr.length === 0) return "System Idle";
        const modeMap = {};
        let maxCount = 0;
        arr.forEach(val => {
            modeMap[val] = (modeMap[val] || 0) + 1;
            if(modeMap[val] > maxCount) { maxCount = modeMap[val]; }
        });
        const candidates = Object.keys(modeMap).filter(key => modeMap[key] === maxCount);
        candidates.sort((a, b) => (severityWeight[b] || 0) - (severityWeight[a] || 0));
        return candidates[0]; 
    }, [severityWeight]);

    const getValidDate = useCallback((ts) => {
        if (!ts) return new Date();
        if (ts < 1000000000000) return new Date(ts * 1000); 
        return new Date(ts);
    }, []);

    // --- CONTINUOUS MODE: DYNAMIC ANOMALY TRIGGER ---
    const triggerContinuousAgenticAI = async (anomalousChunks) => {
        setIsAiLoading(true);
        const finalBpm = finalizeBPM(anomalousChunks);
        setCurrentBpm(finalBpm);
        
        const chunksList = anomalousChunks.map(c => c.prediction);
        const finalResult = calculateAggregatedResult(chunksList);
        
        const recentLogs = history.filter(h => h.patientId === patient.id).slice(0, 15);
        const historyContext = recentLogs.length > 0 ? recentLogs.map(l => `${getValidDate(l.timestamp).toLocaleDateString()}: ${l.classification}`).join("; ") : "No previous history";
        
        const aiResponse = await generateTriageSummary(userRole, finalResult, chunksList, historyContext);
        
        try {
            const parsedAi = typeof aiResponse === 'string' ? JSON.parse(aiResponse) : aiResponse;
            setAiAcuteSummary(`[${parsedAi.risk_level} RISK - ${Math.round(parsedAi.confidence * 100)}%] ${parsedAi.summary}`);
            
            const currentControls = controlsStateRef.current;
            const newAlarm = parsedAi.actuators.alarm || currentControls.alarm;
            const newGsm = parsedAi.actuators.gsm || currentControls.gsm;
            const newLight = parsedAi.actuators.lights || currentControls.light;
            
            update(ref(db, 'controls'), { alarm: newAlarm, gsm: newGsm, light: newLight });
            
            if (newAlarm || newLight || newGsm) {
                playAlertSound();
            }
            
            setCurrentSessionChunks(prev => {
                const newPrev = [...prev];
                if(newPrev.length > 0) {
                    newPrev[newPrev.length - 1] = { ...newPrev[newPrev.length - 1], isTriggerPoint: true };
                }
                return newPrev;
            });

            setAutoSaveCountdown(4);
        } catch (e) {
            console.error("AI JSON Parse Failed:", e);
            setAiAcuteSummary(aiResponse.summary || aiResponse);
        }
        setIsAiLoading(false);
    };

    useEffect(() => {
        if (sessionActive && monitoringMode === 'agentic_continuous' && autoSaveCountdown === null) {
            if (triggerCooldownRef.current > 0) {
                triggerCooldownRef.current -= 1;
            }

            if (currentSessionChunks.length >= anomalySensitivity) {
                const lastChunks = currentSessionChunks.slice(-anomalySensitivity);
                const predictions = lastChunks.map(c => c.prediction);
                
                const isAnomalous = predictions.every(p => p && p !== 'Normal Eupnea' && p !== 'Sighing' && !p.includes('Unknown'));
                const isRecovery = predictions.every(p => p && (p === 'Normal Eupnea' || p === 'Sighing'));

                if (isAnomalous && triggerCooldownRef.current === 0) {
                    triggerContinuousAgenticAI(lastChunks);
                    triggerCooldownRef.current = anomalySensitivity + 1; 
                } else if (isRecovery) {
                    const currentControls = controlsStateRef.current;
                    if (currentControls.alarm || currentControls.light || currentControls.gsm) {
                        update(ref(db, 'controls'), { alarm: false, gsm: false, light: false });
                    }
                }
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSessionChunks, sessionActive, monitoringMode, autoSaveCountdown, anomalySensitivity]);


    // --- FIREBASE LISTENERS ---
    useEffect(() => {
        const controlsRef = ref(db, 'controls');
        const unsubControls = onValue(controlsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setAlarmActive(data.alarm || false);
                setGsmActive(data.gsm || false);
                setLightActive(data.light || false);
            }
        });

        const metricsRef = ref(db, 'hardware_metrics');
        const unsubMetrics = onValue(metricsRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                setHardwareMetrics(prev => ({
                    usrp: { ...prev.usrp, ...data.usrp },
                    rpi: { ...prev.rpi, ...data.rpi }
                }));
            }
        });

        const historyRef = ref(db, 'history');
        const unsubHistory = onValue(historyRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                const historyArray = Object.keys(data).map(key => ({
                    id: key, ...data[key]
                })).reverse();
                setHistory(historyArray);
            } else {
                setHistory([]);
            }
        });

        const sensorRef = ref(db, 'live_sensor');
        const unsubSensor = onValue(sensorRef, (snapshot) => {
            if (snapshot.exists()) {
                const data = snapshot.val();
                
                if (sessionActiveRef.current && data.timestamp && data.timestamp !== lastProcessedTimestampRef.current) {
                    lastProcessedTimestampRef.current = data.timestamp;

                    let formattedWave = [];
                    if (data.wave_data) {
                        const totalSamples = data.wave_data.length;
                        formattedWave = data.wave_data.map((val, i) => ({ 
                            time: ((i / totalSamples) * 10).toFixed(1),
                            value: val 
                        }));
                    }
                    
                    const newChunk = {
                        id: data.timestamp,
                        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
                        prediction: data.predicted_label,
                        waveData: data.wave_data || [],
                        formattedWave: formattedWave,
                        isTriggerPoint: false 
                    };

                    setCurrentSessionChunks(prev => {
                        const updated = [...prev, newChunk];
                        return monitoringModeRef.current === 'agentic_continuous' ? updated.slice(-24) : updated;
                    });
                }
            }
        });

        // NEW LISTENER: Hardware Lock & THEFT DETECTOR
        const lockRef = ref(db, 'hardware_lock');
        const unsubLock = onValue(lockRef, (snapshot) => {
            if (snapshot.exists()) {
                const newLock = snapshot.val();
                
                // --- FIXED LOCK THEFT DETECTOR ---
                // Only trigger theft if: we are recording, AND we did NOT intentionally stop it, AND the lock was removed or taken
                if (sessionActiveRef.current && intentionalStopRef.current === false && (!newLock.is_locked || newLock.host_id !== hostId)) {
                    
                    // 1. Show the beautiful React Modal instead of the blocking browser alert
                    setSessionInterrupted(true);
                    
                    // 2. Turn off local recording
                    setSessionActive(false);
                    update(ref(db, 'system_state'), { session_active: false });
                    
                    // 3. Clear Disconnect Failsafes (since we don't own the lock anymore)
                    onDisconnect(ref(db, 'hardware_lock')).cancel();
                    onDisconnect(ref(db, 'system_state')).cancel();
                    
                    // 4. Save whatever data we had so nothing is lost
                    const chunksToSave = currentSessionChunksRef.current;
                    if (chunksToSave.length > 0) {
                        const predictionsList = chunksToSave.map(c => c.prediction);
                        const finalResult = calculateAggregatedResult(predictionsList);
                        
                        const newLog = {
                            name: patientRef.current.name, 
                            patientId: patientRef.current.id, 
                            classification: finalResult, 
                            action: "Saved (Admin Override Interruption)",
                            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                            timestamp: Date.now(), 
                            totalChunks: chunksToSave.length, 
                            bpm: finalizeBPM(chunksToSave), 
                            chunkBreakdown: predictionsList, 
                            waveData: chunksToSave[chunksToSave.length - 1]?.waveData || [],
                            patientDetails: patientRef.current 
                        };
                        
                        push(ref(db, 'history'), newLog);
                        setCurrentSessionChunks([]);
                        setCurrentBpm(0);
                        setAutoSaveCountdown(null);
                        setAiAcuteSummary('Session interrupted and saved to database.');
                    } else {
                        setAiAcuteSummary('Session aborted before data collection.');
                    }
                }
                
                setHardwareLock(newLock);
            } else {
                setHardwareLock({ is_locked: false, host_id: '', patient_id: '', patient_name: '' });
            }
        });

        return () => { unsubControls(); unsubMetrics(); unsubHistory(); unsubSensor(); unsubLock(); };
    }, [hostId, calculateAggregatedResult, finalizeBPM]); 

    useEffect(() => {
        if (!viewingPatientId && patient.id) {
            setViewingPatientId(patient.id);
        }
    }, [patient.id, viewingPatientId]);

    const severityMap = useMemo(() => ({ 'Biot': -2, 'Slow Bradypnea': -1, 'Normal Eupnea': 0, 'Sighing': 1, 'Fast Tachypnea': 2, 'Kussmaul': 3 }), []);

    const getFilteredTrendData = useCallback((targetPatientId) => {
        const now = new Date();
        const filtered = history.filter(log => {
            if (log.patientId !== targetPatientId) return false;
            if (graphFilter === 'all') return true; 
            const logDate = getValidDate(log.timestamp);
            const logDayName = logDate.toLocaleDateString('en-US', { weekday: 'long' });
            if (graphFilter === 'today') return logDate.toDateString() === now.toDateString();
            if (graphFilter === 'week') return logDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
            return logDayName === graphFilter && logDate >= new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        });

        return filtered.map(log => {
            const logDate = getValidDate(log.timestamp);
            const categoricalTimeLabel = graphFilter === 'all' 
                ? logDate.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute:'2-digit' })
                : logDate.toLocaleTimeString([], { hour: '2-digit', minute:'2-digit' });

            const isAgentic = log.action && (log.action.includes('Agentic AI') || log.action.includes('Continuous Anomaly'));

            return {
                rawTimestamp: log.timestamp,
                time: categoricalTimeLabel, 
                fullDate: logDate.toLocaleDateString() + ' ' + logDate.toLocaleTimeString(),
                severity: severityMap[log.classification] !== undefined ? severityMap[log.classification] : 0,
                label: log.classification,
                chunkBreakdown: log.chunkBreakdown || [],
                isAgentic: isAgentic 
            };
        }).sort((a, b) => a.rawTimestamp - b.rawTimestamp); 
    }, [history, graphFilter, getValidDate, severityMap]);

    const renderCustomDot = (props) => {
        const { cx, cy, payload } = props;
        if (payload.isAgentic) {
            return (
                <g key={`dot-${payload.rawTimestamp}`}>
                    <circle cx={cx} cy={cy} r={6} fill="#ef4444" stroke="#7f1d1d" strokeWidth={2} />
                    <text x={cx} y={cy - 12} fill="#ef4444" fontSize="10" textAnchor="middle" fontWeight="bold">AI</text>
                </g>
            );
        }
        return <circle key={`dot-${payload.rawTimestamp}`} cx={cx} cy={cy} r={3} fill="#fff" />;
    };

    useEffect(() => {
        const fetchDynamicDeepAnalysis = async () => {
            if (!viewingPatientId) return;
            setAiDeepAnalysis('');
            setIsAiLoading(true);
            const filteredData = getFilteredTrendData(viewingPatientId);
            const aiPayload = filteredData.map(d => ({ date: d.fullDate, aggregatedResult: d.label, chunks: d.chunkBreakdown }));
            const aiResponse = await generateDeepAnalysis(userRole, aiPayload, graphFilter);
            setAiDeepAnalysis(aiResponse);
            setIsAiLoading(false);
        };
        if (!isLocked && !showDoctorInitialSelect) fetchDynamicDeepAnalysis();
    }, [viewingPatientId, graphFilter, isLocked, showDoctorInitialSelect, history, getFilteredTrendData, userRole]); 

    // HANDLERS
    const handleDoctorLogin = (e) => {
        e.preventDefault();
        if (doctorPin === '10232') { 
            setIsLocked(false); 
            setUserRole('doctor'); 
            setLoginError(''); 
            setShowDoctorInitialSelect(true); 
        } 
        else { setLoginError('Invalid PIN'); setTimeout(() => setLoginError(''), 1500); }
    };

    const handlePatientLogin = (e) => {
        e.preventDefault();
        const enteredId = patientLoginId.trim().toUpperCase(); 
        if(enteredId === '') return;
        const existingLog = history.find(log => log.patientId === enteredId);
        if (existingLog || enteredId === patient.id) {
            setViewingPatientId(enteredId); 
            if (existingLog && existingLog.patientDetails) setPatient(existingLog.patientDetails);
            setUserRole('patient'); setIsLocked(false); setLoginError('');
        } else {
            setLoginError('Patient ID not found.'); setTimeout(() => setLoginError(''), 2000);
        }
    };

    const toggleSession = async () => {
        if (!sessionActive) {
            // --- NEW LOCK CHECK ---
            if (hardwareLock.is_locked && hardwareLock.host_id !== hostId) {
                alert(`Cannot start scan. The USRP apparatus is currently in use by another terminal serving patient: ${hardwareLock.patient_name}`);
                return;
            }
            
            if (currentSessionChunks.length > 0) {
                const confirmDiscard = window.confirm("You have an unsaved session. Starting a new scan will discard it. Do you want to continue without saving?");
                if (!confirmDiscard) return; 
            }

            // --- NEW LOCK CLAIM ---
            const lockRef = ref(db, 'hardware_lock');
            const stateRef = ref(db, 'system_state');

            update(lockRef, {
                is_locked: true,
                host_id: hostId,
                patient_id: patient.id,
                patient_name: patient.name
            });
            
            // AUTOMATIC FAILSAFE: If browser closes or WiFi drops, Firebase will auto-unlock this!
            onDisconnect(lockRef).set({
                is_locked: false,
                host_id: '',
                patient_id: '',
                patient_name: ''
            });
            onDisconnect(stateRef).update({ session_active: false });
            // ----------------------

            update(ref(db, 'controls'), { alarm: false, gsm: false, light: false });

            setSessionActive(true);
            setCurrentSessionChunks([]);
            setCurrentBpm(0); 
            setViewingPatientId(patient.id); 
            setAiAcuteSummary('Analyzing live telemetry...');
            update(stateRef, { session_active: true });
        } else {
            
            // --- FIX 1: RAISE THE INTENTIONAL STOP FLAG ---
            intentionalStopRef.current = true;

            const lockRef = ref(db, 'hardware_lock');
            const stateRef = ref(db, 'system_state');

            setSessionActive(false);
            update(stateRef, { session_active: false });
            
            // --- NEW LOCK RELEASE ---
            update(lockRef, {
                is_locked: false,
                host_id: '',
                patient_id: '',
                patient_name: ''
            });

            // CANCEL the failsafe because we stopped it normally
            onDisconnect(lockRef).cancel();
            onDisconnect(stateRef).cancel();

            // Lower the flag after 2 seconds (gives Firebase time to process)
            setTimeout(() => { intentionalStopRef.current = false; }, 2000);
            // ------------------------
            
            if (currentSessionChunks.length > 0) {
                const finalBpm = finalizeBPM(currentSessionChunks);
                setCurrentBpm(finalBpm);
                setIsAiLoading(true);
                const chunksList = currentSessionChunks.map(c => c.prediction);
                const finalResult = calculateAggregatedResult(chunksList);
                const recentLogs = history.filter(h => h.patientId === patient.id).slice(0, 15);
                const historyContext = recentLogs.length > 0 ? recentLogs.map(l => `${getValidDate(l.timestamp).toLocaleDateString()}: ${l.classification}`).join("; ") : "No previous history";
                
                const aiResponse = await generateTriageSummary(userRole, finalResult, chunksList, historyContext);
                
                if (userRole === 'doctor') {
                    try {
                        const parsedAi = typeof aiResponse === 'string' ? JSON.parse(aiResponse) : aiResponse;
                        setAiAcuteSummary(`[${parsedAi.risk_level} RISK - ${Math.round(parsedAi.confidence * 100)}%] ${parsedAi.summary}`);
                        
                        if (monitoringMode === 'agentic_discrete') {
                            const currentControls = controlsStateRef.current;
                            update(ref(db, 'controls'), { 
                                alarm: parsedAi.actuators.alarm || currentControls.alarm, 
                                gsm: parsedAi.actuators.gsm || currentControls.gsm, 
                                light: parsedAi.actuators.lights || currentControls.light 
                            });
                            setAutoSaveCountdown(4);
                        } else {
                            setAutoSaveCountdown(null);
                        }
                    } catch (e) {
                        console.error("AI JSON Parse Failed:", e);
                        setAiAcuteSummary(aiResponse.summary || aiResponse);
                    }
                } else {
                    setAiAcuteSummary(aiResponse); 
                }
                setIsAiLoading(false);
            } else {
                setAiAcuteSummary('Session aborted before data collection.');
            }
        }
    };

    const saveSessionSummary = (saveReason = 'manual') => {
        if (currentSessionChunks.length === 0) return;
        
        const isContinuousAutoSave = (saveReason === 'continuous');
        const isDiscreteAgenticAutoSave = (saveReason === 'discrete_agentic');
        
        const chunksToSave = isContinuousAutoSave ? currentSessionChunks.slice(-anomalySensitivity) : currentSessionChunks;
        const predictionsList = chunksToSave.map(c => c.prediction);
        const finalResult = calculateAggregatedResult(predictionsList);
        
        let actionStr = "Acknowledged";
        
        if(userRole === 'doctor') {
            let activeControls = [];
            if(alarmActive) activeControls.push("Alarm");
            if(gsmActive) activeControls.push("GPS + Call");
            if(lightActive) activeControls.push("Lights");
            actionStr = activeControls.length > 0 ? `Acknowledged + (${activeControls.join(", ")})` : `Acknowledged`;
            
            if (isContinuousAutoSave) {
                actionStr += " [Continuous Anomaly Auto-Saved]";
            } else if (isDiscreteAgenticAutoSave) {
                actionStr += " (Saved via Agentic AI)";
            }
        }

        const newLog = {
            name: patient.name, patientId: patient.id, 
            classification: finalResult, action: actionStr,
            time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            timestamp: Date.now(), 
            totalChunks: chunksToSave.length, 
            bpm: currentBpm,
            chunkBreakdown: predictionsList, 
            waveData: chunksToSave[chunksToSave.length - 1]?.waveData || [],
            patientDetails: patient 
        };
        
        push(ref(db, 'history'), newLog);
        
        if (!isContinuousAutoSave) {
            setCurrentSessionChunks([]);
            setCurrentBpm(0);
        }
        
        setAutoSaveCountdown(null); 
        setAiAcuteSummary('Saved to database. System ready.');
    };

    const toggleControl = (controlType, currentState) => {
        if(userRole !== 'doctor') return; 
        update(ref(db, 'controls'), { [controlType]: !currentState });
    };

    const handleProfileUpdate = (e) => {
    e.preventDefault();
    
    // 1. Check if we are updating an existing patient ID
    if (editPatientForm.id && editPatientForm.id.trim() !== '') {
        const targetId = editPatientForm.id.toUpperCase();
        
        // Find ALL history logs for this patient so we can update them in the database
        const existingLogs = history.filter(h => h.patientId === targetId);
        
        if (existingLogs.length > 0) {
            // FIX: Add the confirmation dialog you requested
            const confirmUpdate = window.confirm(`Are you sure you want to update the profile for ${targetId} in the database?`);
            if (!confirmUpdate) return; // Stop if they click Cancel

            // Merge the old details with the NEW edits from the form
            const updatedPatient = { ...existingLogs[0].patientDetails, ...editPatientForm };
            
            // FIX: Create a multi-path batch update for Firebase
            const databaseUpdates = {};
            existingLogs.forEach(log => {
                // Update the nested patient details
                databaseUpdates[`history/${log.id}/patientDetails`] = updatedPatient;
                // Update the top-level name (used for your quick search list)
                databaseUpdates[`history/${log.id}/name`] = updatedPatient.name; 
            });

            // Send the update directly to Firebase Realtime Database
            update(ref(db), databaseUpdates).then(() => {
                // Once Firebase successfully saves, update the UI
                setPatient(updatedPatient); 
                setViewingPatientId(targetId);
                setShowProfileModal(false);
                setShowDoctorInitialSelect(false); 
            }).catch(error => {
                console.error("Database update failed:", error);
                alert("Failed to save changes to the database.");
            });
            
            return; // Exit the function early so we don't hit the "New Patient" logic
        }
    }
    
    // 2. Logic for registering a completely NEW patient
    // (Note: New patients are saved to Firebase automatically when you record their first session)
    let finalId = editPatientForm.id;
    if (!finalId) {
        const initial = editPatientForm.name ? editPatientForm.name.charAt(0).toUpperCase() : 'X';
        const randomNum = Math.floor(1000 + Math.random() * 9000);
        finalId = `P-${initial}-${randomNum}`;
    }
    
    const newPatient = { ...editPatientForm, id: finalId.toUpperCase() };
    setPatient(newPatient);
    setViewingPatientId(newPatient.id);
    setShowProfileModal(false);
    setShowDoctorInitialSelect(false); 
};

   const activatePatientForRecording = (patientId) => {
    // FIX: Check for unsaved data before allowing the switch
    if (currentSessionChunks.length > 0) {
        const confirmDiscard = window.confirm("You have unsaved session data for the current patient. Discard and switch anyway?");
        if (!confirmDiscard) return; // Stop the switch if they don't want to discard
    }

    update(ref(db, 'controls'), { alarm: false, gsm: false, light: false });

    const logWithDetails = history.find(h => h.patientId === patientId && h.patientDetails);
    if (logWithDetails) {
        setPatient(logWithDetails.patientDetails);
    } else {
        const log = history.find(h => h.patientId === patientId);
        setPatient({ ...patient, id: patientId, name: log.name });
    }
    
    setViewingPatientId(patientId);
    
    // FIX: Clear the old patient's session state!
    setCurrentSessionChunks([]);
    setCurrentBpm(0);
    setAiAcuteSummary('Standby. Start a session to generate AI insights.');

    setShowDoctorInitialSelect(false); 
    setActiveTab('dashboard');
    window.scrollTo({ top: 0, behavior: 'smooth' });
};

    const toggleGroupExpand = (patientId) => { setExpandedGroups(prev => ({ ...prev, [patientId]: !prev[patientId] })); };
    const clearHistory = () => { if(window.confirm("Clear all history logs from database?")) remove(ref(db, 'history')); };

    const updatePatientFilter = (patientId, key, value) => {
        setDbPatientFilters(prev => ({
            ...prev,
            [patientId]: { ...(prev[patientId] || { type: 'all', sort: 'desc' }), [key]: value }
        }));
    };

    const isDoctor = userRole === 'doctor';
    const now = new Date();
    
    // THEME DICTIONARY
    const t = useMemo(() => isClinicalTheme ? {
        textPrimary: 'text-blue-600',
        textSecondary: 'text-teal-600',
        bgPrimary: 'bg-blue-600',
        bgSecondary: 'bg-teal-600',
        borderPrimary: 'border-blue-500',
        borderSecondary: 'border-teal-500',
        gradient: 'from-blue-600 to-teal-600',
        shadow: 'shadow-lg shadow-blue-500/10',
        activeMenuBg: 'bg-blue-50 text-blue-600 border-blue-200',
        appBg: 'bg-slate-50',
        cardBg: 'bg-white',
        innerBg: 'bg-slate-50',
        mainText: 'text-slate-900',
        subText: 'text-slate-500',
        borderColor: 'border-slate-200',
        imgOpacity: 'opacity-[0.03]', 
        chartGrid: '#e2e8f0',
        chartText: '#64748b',
        chartLineNormal: '#10b981', 
        chartLineAbnormal: '#ef4444',
        tooltipBg: '#ffffff',
        tooltipBorder: '#e2e8f0',
        tooltipText: '#0f172a'
    } : {
        textPrimary: 'text-neonBlue',
        textSecondary: 'text-neonPink',
        bgPrimary: 'bg-neonBlue',
        bgSecondary: 'bg-neonPink',
        borderPrimary: 'border-neonBlue',
        borderSecondary: 'border-neonPink',
        gradient: 'from-neonBlue to-neonPink',
        shadow: 'shadow-[0_0_40px_rgba(0,240,255,0.4)]',
        activeMenuBg: 'bg-neonBlue/20 text-neonBlue border-transparent',
        appBg: 'bg-[#0b0c15]',
        cardBg: 'bg-[#111827]/90',
        innerBg: 'bg-gray-900/60',
        mainText: 'text-white',
        subText: 'text-gray-400',
        borderColor: 'border-gray-800',
        imgOpacity: 'opacity-20',
        chartGrid: '#222',
        chartText: '#888',
        chartLineNormal: '#4ade80', 
        chartLineAbnormal: '#ff007f', 
        tooltipBg: '#111111',
        tooltipBorder: '#333333',
        tooltipText: '#ffffff'
    }, [isClinicalTheme]);

    const getGraphColor = useCallback((prediction) => {
        if (!prediction) return t.chartText;
        return prediction.includes('Normal') || prediction.includes('Eupnea') ? t.chartLineNormal : t.chartLineAbnormal;
    }, [t]);

    let dbFilteredHistory = history.filter(log => {
        if (dbGlobalDateFilter !== 'all') {
            const logDate = getValidDate(log.timestamp);
            if (dbGlobalDateFilter === 'today' && logDate.toDateString() !== now.toDateString()) return false;
            if (dbGlobalDateFilter === 'week' && logDate < new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)) return false;
        }
        if (isDoctor) {
            let matches = log.name.toLowerCase().includes(dbSearchTerm.toLowerCase()) || (log.patientId && log.patientId.toLowerCase().includes(dbSearchTerm.toLowerCase()));
            if (dbShowOnlyAI) {
                matches = matches && log.action && (log.action.includes('Agentic AI') || log.action.includes('Continuous Anomaly'));
            }
            return matches;
        }
        return log.patientId === viewingPatientId;
    });

    const groupedDbHistory = dbFilteredHistory.reduce((acc, log) => {
        const key = log.patientId || log.name; 
        if (!acc[key]) acc[key] = [];
        acc[key].push(log); return acc;
    }, {});

    const uniquePatientsList = Object.values(history.reduce((acc, log) => {
        if(log.patientId && !acc[log.patientId]) {
            acc[log.patientId] = { id: log.patientId, name: log.name };
        }
        return acc;
    }, {})).filter(p => p.name.toLowerCase().includes(quickSearchTerm.toLowerCase()) || p.id.toLowerCase().includes(quickSearchTerm.toLowerCase()));

    const activeChunk = currentSessionChunks[currentSessionChunks.length - 1];
    const aggResult = calculateAggregatedResult(currentSessionChunks.map(c => c.prediction));
    const viewingPatientData = history.find(h => h.patientId === viewingPatientId) || patient;


    // ==========================================
    // RENDER
    // ==========================================
    if (showLandingPage) {
        return <FYPLandingPage onProceed={() => setShowLandingPage(false)} t={t} />;
    }
    if (isLocked) {
        return <LockScreen 
            doctorPin={doctorPin} setDoctorPin={setDoctorPin} handleDoctorLogin={handleDoctorLogin}
            patientLoginId={patientLoginId} setPatientLoginId={setPatientLoginId} handlePatientLogin={handlePatientLogin}
            loginError={loginError} t={t}
        />;
    }

    if (showDoctorInitialSelect) {
        return (
            <div className={`min-h-screen ${t.appBg} flex flex-col items-center justify-center p-4 md:p-6 relative font-sans transition-colors duration-500`}>
                <div className="absolute inset-0 z-0"><img src={dashboardBg} alt="BG" className={`w-full h-full object-cover transition-opacity duration-500 ${t.imgOpacity}`} /></div>
                <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`max-w-2xl w-full ${t.cardBg} border ${t.borderColor} p-6 md:p-10 rounded-3xl relative z-10 ${t.shadow} transition-colors duration-500`}>
                    <h2 className={`text-2xl md:text-3xl font-black ${t.mainText} mb-2 flex items-center gap-3`}><Users className={t.textPrimary} /> Select Active Patient</h2>
                    <p className={`${t.subText} mb-8 text-sm md:text-base`}>Please select a patient from the database to begin telemetry monitoring, or register a new admission.</p>
                    
                    <div className={`${t.innerBg} p-4 md:p-6 rounded-2xl border ${t.borderColor} mb-6 flex flex-col h-[300px] transition-colors duration-500`}>
                        <div className="relative mb-4">
                            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${t.subText}`} size={16} />
                            <input type="text" placeholder="Search by Name or Patient ID..." value={quickSearchTerm} onChange={(e) => setQuickSearchTerm(e.target.value)} className={`w-full ${t.cardBg} border ${t.borderColor} ${t.mainText} pl-10 pr-4 py-3 rounded-xl text-sm outline-none focus:${t.borderPrimary} transition-colors`} />
                        </div>
                        <div className="flex-1 overflow-y-auto pr-2 space-y-2 scrollbar-thin scrollbar-thumb-gray-700">
                            {uniquePatientsList.map(p => (
                                <div key={p.id} onClick={() => activatePatientForRecording(p.id)} className={`flex justify-between items-center p-4 rounded-xl border ${t.cardBg} ${t.borderColor} hover:${t.bgPrimary}/10 hover:${t.borderPrimary}/50 cursor-pointer transition-all group`}>
                                    <div className="flex flex-col">
                                        <span className={`text-sm font-bold ${t.mainText} group-hover:${t.textPrimary} transition-colors`}>{p.name}</span>
                                        <span className={`text-xs ${t.subText} font-mono mt-1`}>{p.id}</span>
                                    </div>
                                    <span className={`px-3 py-1 ${t.innerBg} ${t.subText} group-hover:${t.bgPrimary} group-hover:text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-2`}>Load Dashboard <Activity size={14}/></span>
                                </div>
                            ))}
                            {uniquePatientsList.length === 0 && <div className={`h-full flex items-center justify-center ${t.subText} italic`}>No patients found in database.</div>}
                        </div>
                    </div>

                    <button onClick={() => { setEditPatientForm({...patient, id: '', name: ''}); setShowProfileModal(true); }} className={`w-full bg-transparent border-2 ${t.borderPrimary} ${t.textPrimary} hover:${t.bgPrimary} hover:text-white font-bold py-4 rounded-xl transition-all flex justify-center items-center gap-2 uppercase tracking-widest text-sm md:text-base`}><Edit size={18}/> Register New Patient</button>
                </motion.div>
                
                <AnimatePresence>
                    {showProfileModal && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
                            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className={`${t.cardBg} border ${t.borderColor} p-6 md:p-8 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${t.shadow} relative transition-colors duration-500`}>
                                <div className={`flex justify-between items-center mb-6 border-b ${t.borderColor} pb-4`}>
                                    <h2 className={`text-xl md:text-2xl font-bold ${t.mainText}`}>Register / Edit Patient Profile</h2>
                                    <button onClick={() => setShowProfileModal(false)} className={`${t.subText} hover:${t.mainText} ${t.innerBg} p-2 rounded-full`}><X size={20} /></button>
                                </div>
                                <form onSubmit={handleProfileUpdate} className="space-y-5">
                                    <p className={`text-xs ${t.subText} mb-4`}>Leave ID blank to generate a new one, or enter an existing ID to load a patient record.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="md:col-span-2"><label className={`text-xs ${t.textPrimary} font-bold uppercase ml-1`}>Patient ID</label><input type="text" value={editPatientForm.id || ''} onChange={(e) => setEditPatientForm({...editPatientForm, id: e.target.value})} placeholder="e.g. P-F-1234 (Leave blank for new)" className={`mt-1 w-full ${t.innerBg} border ${t.borderPrimary} p-3 rounded-xl ${t.mainText} outline-none placeholder-gray-500 uppercase transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Patient Name</label><input type="text" value={editPatientForm.name} onChange={(e) => setEditPatientForm({...editPatientForm, name: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} required/></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Father's Name</label><input type="text" value={editPatientForm.fatherName} onChange={(e) => setEditPatientForm({...editPatientForm, fatherName: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} required/></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Phone Number</label><input type="tel" value={editPatientForm.phone} onChange={(e) => setEditPatientForm({...editPatientForm, phone: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Email</label><input type="email" value={editPatientForm.email} onChange={(e) => setEditPatientForm({...editPatientForm, email: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div className="md:col-span-2"><label className={`text-xs ${t.subText} uppercase ml-1`}>Full Address</label><input type="text" value={editPatientForm.address} onChange={(e) => setEditPatientForm({...editPatientForm, address: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>City</label><input type="text" value={editPatientForm.city} onChange={(e) => setEditPatientForm({...editPatientForm, city: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Country</label><input type="text" value={editPatientForm.country} onChange={(e) => setEditPatientForm({...editPatientForm, country: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                    </div>
                                    <button type="submit" className={`w-full ${t.bgPrimary} text-white font-bold py-4 rounded-xl mt-6 hover:opacity-90 transition-all ${t.shadow} flex justify-center items-center gap-2`}><Save size={20}/> Load / Save Profile</button>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${t.appBg} ${t.mainText} p-4 md:pl-24 pb-24 md:pb-4 font-sans relative overflow-x-hidden transition-colors duration-500`}>
            
            {/* FIX 2: THE NEW THEFT DETECTOR MODAL */}
            <AnimatePresence>
                {sessionInterrupted && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[999] flex items-center justify-center bg-red-900/80 backdrop-blur-md p-4">
                        <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className={`${t.cardBg} border-2 border-red-500 p-8 rounded-3xl w-full max-w-md text-center shadow-2xl`}>
                            <ShieldCheck size={56} className="text-red-500 mx-auto mb-4" />
                            <h2 className={`text-2xl font-black text-red-500 mb-2 tracking-widest`}>SESSION OVERRIDDEN</h2>
                            <p className={`${t.subText} mb-6 text-sm leading-relaxed`}>An admin has force-unlocked the hardware from another terminal. Your current session data has been saved automatically to prevent data loss.</p>
                            <button onClick={() => setSessionInterrupted(false)} className={`w-full bg-red-500 text-white font-bold py-3 rounded-xl hover:bg-red-600 transition shadow-lg shadow-red-500/20`}>ACKNOWLEDGE</button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            <div className="absolute inset-0 z-0 pointer-events-none"><img src={dashboardBg} alt="BG" className={`w-full h-full object-cover transition-opacity duration-500 ${t.imgOpacity}`} /></div>

            {/* DESKTOP SIDEBAR */}
            <div className={`hidden md:flex fixed left-0 top-0 h-full w-16 hover:w-64 ${t.cardBg} border-r ${t.borderColor} z-50 flex-col py-6 transition-all duration-300 overflow-hidden group shadow-2xl`}>
                <div className="flex items-center px-4 mb-10 whitespace-nowrap">
                    <div className={`min-w-[32px] h-[32px] flex items-center justify-center rounded-lg border ${isDoctor ? `${t.bgPrimary}/10 ${t.textPrimary} ${t.borderPrimary}` : `${t.bgSecondary}/10 ${t.textSecondary} ${t.borderSecondary}`}`}>
                        <Activity size={18} />
                    </div>
                    <span className={`ml-4 font-black tracking-widest ${t.mainText} opacity-0 group-hover:opacity-100 transition-opacity duration-300`}>RF SENSING</span>
                </div>
                
                <nav className="flex-1 flex flex-col gap-2 px-3 w-full">
                    <button onClick={() => setActiveTab('dashboard')} title="Telemetry Dashboard" className={`flex items-center w-full p-3 rounded-xl transition-colors border whitespace-nowrap ${activeTab === 'dashboard' ? (isDoctor ? t.activeMenuBg : `${t.bgSecondary}/10 border-transparent ${t.textSecondary}`) : `border-transparent ${t.subText} hover:${t.innerBg} hover:${t.mainText}`}`}>
                        <Activity size={20} className="min-w-[20px]" />
                        <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">Live Dashboard</span>
                    </button>
                    {isDoctor && (
                        <button onClick={() => setActiveTab('database')} title="Master Patient Database" className={`flex items-center w-full p-3 rounded-xl transition-colors border whitespace-nowrap ${activeTab === 'database' ? t.activeMenuBg : `border-transparent ${t.subText} hover:${t.innerBg} hover:${t.mainText}`}`}>
                            <Users size={20} className="min-w-[20px]" />
                            <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">Patient Database</span>
                        </button>
                    )}
                    {isDoctor && (
                        <button onClick={() => setActiveTab('devices')} title="Device & Hardware Management" className={`flex items-center w-full p-3 rounded-xl transition-colors border whitespace-nowrap ${activeTab === 'devices' ? t.activeMenuBg : `border-transparent ${t.subText} hover:${t.innerBg} hover:${t.mainText}`}`}>
                            <Server size={20} className="min-w-[20px]" />
                            <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">Device Status</span>
                        </button>
                    )}
                    <button onClick={() => setActiveTab('docs')} title="Project Documentation" className={`flex items-center w-full p-3 rounded-xl transition-colors border whitespace-nowrap ${activeTab === 'docs' ? (isDoctor ? t.activeMenuBg : `${t.bgSecondary}/10 border-transparent ${t.textSecondary}`) : `border-transparent ${t.subText} hover:${t.innerBg} hover:${t.mainText}`}`}>
                        <BookOpen size={20} className="min-w-[20px]" />
                        <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">Documentation</span>
                    </button>
                </nav>

                <div className="px-3 w-full flex flex-col gap-2">
                    {isDoctor && (
                        <div className={`flex flex-col gap-1 p-2 rounded-xl border ${t.borderColor} ${t.innerBg} mb-2`}>
                            <span className={`text-[9px] font-bold uppercase tracking-wider ${t.subText} ml-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap`}>Mode Select</span>
                            
                            <button onClick={() => setMonitoringMode('manual')} className={`flex items-center w-full p-2 rounded-lg transition-colors whitespace-nowrap overflow-hidden ${monitoringMode === 'manual' ? `${t.bgPrimary} text-white` : `text-gray-500 hover:${t.mainText}`}`}>
                                <ActivitySquare size={16} className="min-w-[16px]" />
                                <span className="ml-3 font-bold text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Manual Spot-Check</span>
                            </button>
                            
                            <button onClick={() => setMonitoringMode('agentic_discrete')} className={`flex items-center w-full p-2 rounded-lg transition-colors whitespace-nowrap overflow-hidden ${monitoringMode === 'agentic_discrete' ? 'bg-purple-600 text-white' : `text-gray-500 hover:${t.mainText}`}`}>
                                <BrainCircuit size={16} className="min-w-[16px]" />
                                <span className="ml-3 font-bold text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Agentic AI (Discrete)</span>
                            </button>
                            
                            <button onClick={() => setMonitoringMode('agentic_continuous')} className={`flex items-center w-full p-2 rounded-lg transition-colors whitespace-nowrap overflow-hidden ${monitoringMode === 'agentic_continuous' ? 'bg-green-600 text-white' : `text-gray-500 hover:${t.mainText}`}`}>
                                <Activity size={16} className="min-w-[16px]" />
                                <span className="ml-3 font-bold text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">Autonomous ICU</span>
                            </button>

                            {monitoringMode === 'agentic_continuous' && (
                                <div className={`mt-2 p-2 rounded-lg border ${t.borderColor} ${t.appBg} opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col gap-1`}>
                                    <span className={`text-[9px] uppercase font-bold ${t.subText}`}>AI Sensitivity:</span>
                                    <select 
                                        value={anomalySensitivity} 
                                        onChange={(e) => setAnomalySensitivity(Number(e.target.value))} 
                                        className={`bg-transparent text-xs ${t.mainText} outline-none cursor-pointer`}
                                    >
                                        <option value={2} className="bg-gray-900 text-white">High (2 Windows)</option>
                                        <option value={3} className="bg-gray-900 text-white">Standard (3 Windows)</option>
                                        <option value={5} className="bg-gray-900 text-white">Low (5 Windows)</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    <button onClick={() => setIsClinicalTheme(!isClinicalTheme)} title="Toggle Clinical/Neon Theme" className={`flex items-center w-full p-3 ${t.subText} hover:${t.innerBg} hover:${t.mainText} rounded-xl transition-colors whitespace-nowrap border border-transparent`}>
                        <Palette size={20} className="min-w-[20px]" />
                        <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">Toggle Theme</span>
                    </button>

                    <button onClick={() => {setIsLocked(true); setUserRole(null); setShowDoctorInitialSelect(false);}} title="Secure Logout" className={`flex items-center w-full p-3 ${t.subText} hover:bg-red-500/10 hover:text-red-500 rounded-xl transition-colors whitespace-nowrap border border-transparent`}>
                        <LogOut size={20} className="min-w-[20px]" />
                        <span className="ml-4 font-bold text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300 uppercase">Logout Session</span>
                    </button>
                </div>
            </div>

            {/* MOBILE BOTTOM NAV */}
            <div className={`md:hidden fixed bottom-0 left-0 w-full ${t.cardBg} border-t ${t.borderColor} z-50 flex justify-around items-center p-3 pb-safe`}>
                <button onClick={() => setActiveTab('dashboard')} className={`p-2 rounded-xl flex flex-col items-center ${activeTab === 'dashboard' ? t.textPrimary : t.subText}`}>
                    <Activity size={20} />
                    <span className="text-[10px] mt-1 font-bold">Dash</span>
                </button>
                {isDoctor && (
                    <button onClick={() => setActiveTab('database')} className={`p-2 rounded-xl flex flex-col items-center ${activeTab === 'database' ? t.textPrimary : t.subText}`}>
                        <Users size={20} />
                        <span className="text-[10px] mt-1 font-bold">Data</span>
                    </button>
                )}
                {isDoctor && (
                    <button onClick={() => setActiveTab('devices')} className={`p-2 rounded-xl flex flex-col items-center ${activeTab === 'devices' ? t.textPrimary : t.subText}`}>
                        <Server size={20} />
                        <span className="text-[10px] mt-1 font-bold">Nodes</span>
                    </button>
                )}
                <button onClick={() => setActiveTab('docs')} className={`p-2 rounded-xl flex flex-col items-center ${activeTab === 'docs' ? t.textPrimary : t.subText}`}>
                    <BookOpen size={20} />
                    <span className="text-[10px] mt-1 font-bold">Docs</span>
                </button>
                <button onClick={() => {setIsLocked(true); setUserRole(null); setShowDoctorInitialSelect(false);}} className={`p-2 rounded-xl flex flex-col items-center text-red-500`}>
                    <LogOut size={20} />
                    <span className="text-[10px] mt-1 font-bold">Exit</span>
                </button>
            </div>

            <div className="relative z-10 max-w-[1600px] mx-auto flex flex-col gap-6 pt-2">
                <header className={`flex flex-col md:flex-row justify-between items-start md:items-end pb-4 border-b ${t.borderColor} transition-colors duration-500 gap-4 md:gap-0`}>
                    <div>
                        <h1 className={`text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r ${t.gradient}`}>RF Respiratory Dashboard</h1>
                        <p className={`${t.subText} text-sm mt-1`}>{isDoctor ? 'Clinical Command Center' : 'Patient Read-Only Terminal'}</p>
                    </div>
                    {activeTab === 'dashboard' && (
                        <div className="flex items-center gap-4">
                            <span className={`px-4 py-1.5 rounded-full text-xs font-bold border flex items-center gap-2 ${sessionActive ? 'bg-green-500/10 text-green-500 border-green-500/50' : `${t.innerBg} ${t.subText} ${t.borderColor}`}`}>
                                {sessionActive && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>}
                                {!sessionActive && 'SYSTEM IDLE'}
                                {sessionActive && monitoringMode === 'agentic_continuous' ? 'ICU STREAM ACTIVE' : sessionActive ? 'LIVE STREAM ACTIVE' : ''}
                            </span>
                        </div>
                    )}
                </header>

                {activeTab === 'dashboard' && (
                    <>
                        {/* SECTION: REAL-TIME TELEMETRY & CONTROLS */}
                        <div>
                            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-4 flex items-center gap-2`}><Activity size={16}/> Real-Time Telemetry & Controls</h2>
                            <div className="grid grid-cols-12 gap-4">
                                
                                {/* LEFT PANEL: PATIENT PROFILE & QUICK SWITCH */}
                                <div className="col-span-12 lg:col-span-3 flex flex-col gap-4">
                                    <div className={`${t.cardBg} border ${t.borderColor} rounded-2xl p-5 relative shadow-lg transition-colors duration-500`}>
                                        {isDoctor && (
                                            <button title="Register New / Edit Profile" onClick={() => { setEditPatientForm({...patient}); setShowProfileModal(true); }} className={`absolute top-4 right-4 p-2 ${t.innerBg} rounded-full hover:${t.bgPrimary} hover:text-white transition z-20`}><Edit size={14} /></button>
                                        )}
                                        <div className="flex items-center gap-4 mb-4">
                                            <div className={`w-14 h-14 rounded-full border-2 flex items-center justify-center ${t.innerBg} ${isDoctor ? t.borderPrimary : t.borderSecondary}`}>
                                                <User size={24} className={t.subText} />
                                            </div>
                                            <div>
                                                <h3 className={`text-xl font-bold ${t.mainText}`}>{patient.name}</h3>
                                                <p className={`text-xs ${t.textPrimary} font-mono`}>{patient.id}</p>
                                            </div>
                                        </div>
                                        
                                        <div className={`grid grid-cols-1 gap-2 text-xs ${t.mainText} border-t ${t.borderColor} pt-4 transition-colors duration-500`}>
                                            <div className="flex flex-col gap-1">
                                                <p className="flex items-center gap-2"><User size={12} className={t.subText}/> <span className={`${t.subText} w-12`}>Father:</span> {patient.fatherName}</p>
                                                <p className="flex items-center gap-2"><Mail size={12} className={t.subText}/> <span className={`${t.subText} w-12`}>Email:</span> {patient.email}</p>
                                                <p className="flex items-center gap-2"><Phone size={12} className={t.subText}/> <span className={`${t.subText} w-12`}>Phone:</span> {patient.phone}</p>
                                                <p className="flex items-start gap-2 mt-1"><Map size={12} className={`${t.subText} mt-1`}/> <span className={`${t.subText} w-12`}>Address:</span> <span className="flex-1">{patient.address}, {patient.city}, {patient.country}</span></p>
                                            </div>
                                            
                                            <div className={`mt-3 p-3 ${t.innerBg} rounded-lg border ${t.borderColor} flex justify-between items-center`}>
                                                <span className={t.subText}>Live Status:</span>
                                                <span className={`font-bold text-sm ${aggResult.includes('Normal') ? 'text-green-500' : 'text-red-500'}`}>{aggResult}</span>
                                            </div>
                                        </div>

                                        {/* DYNAMIC RECORDING BUTTON WITH LOCK AWARENESS */}
                                        {hardwareLock.is_locked && hardwareLock.host_id !== hostId ? (
                                            <div className="flex flex-col gap-2 mt-6">
                                                <button disabled className={`w-full py-3 rounded-xl font-bold flex justify-center items-center gap-2 transition-all bg-gray-500/20 text-gray-500 border border-gray-500/50 cursor-not-allowed shadow-inner text-xs sm:text-base`}>
                                                    <Lock size={16}/> IN USE: {hardwareLock.patient_name.toUpperCase()}
                                                </button>
                                                
                                                {/* ADMIN OVERRIDE FAILSAFE */}
                                                {isDoctor && (
                                                    <button 
                                                        onClick={() => {
                                                            if(window.confirm("Are you sure you want to force unlock? This will safely auto-save the active session for the other doctor, then grant you control.")) {
                                                                update(ref(db, 'hardware_lock'), { is_locked: false, host_id: '', patient_id: '', patient_name: '' });
                                                            }
                                                        }} 
                                                        className="text-red-500 text-[10px] font-bold tracking-widest uppercase hover:underline text-center"
                                                    >
                                                        Force Unlock (Admin Override)
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <button title={sessionActive ? "Stop the live USRP stream" : `Start scanning RF data for ${patient.name}`} onClick={toggleSession} className={`w-full py-3 mt-6 rounded-xl font-bold flex justify-center items-center gap-2 transition-all ${sessionActive ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/20'}`}>
                                                {sessionActive ? <><Square size={16} fill="currentColor"/> STOP RECORDING</> : <><Play size={16} fill="currentColor"/> START LIVE SCAN</>}
                                            </button>
                                        )}
                                        {/* NEW: Explicit Save Button when there are unsaved chunks */}
                                       
                                        {!sessionActive && currentSessionChunks.length > 0 && (
                                            <button 
                                                onClick={() => saveSessionSummary('manual')} 
                                                className={`w-full py-3 mt-3 rounded-xl font-bold flex justify-center items-center gap-2 transition-all bg-blue-500 hover:bg-blue-600 text-white shadow-lg shadow-blue-500/20`}
                                            >
                                                <Save size={16} /> SAVE SESSION TO DATABASE
                                            </button>
                                        )}
                                        {/* AGENTIC AI OVERRIDE COUNTDOWN BAR */}
                                        {autoSaveCountdown !== null && (
                                            <div className={`w-full mt-2 border border-blue-500/50 p-3 rounded-xl text-center bg-blue-500/10`}>
                                                <span className="text-blue-500 text-[10px] font-bold uppercase tracking-widest animate-pulse">
                                                    Agentic AI Action Pending<br/>Auto-saving in {autoSaveCountdown}s
                                                </span>
                                                <div className="w-full bg-gray-800/20 h-1 mt-2 rounded-full overflow-hidden">
                                                    <div className="bg-blue-500 h-full transition-all duration-1000 ease-linear" style={{ width: `${(autoSaveCountdown / 4) * 100}%` }}></div>
                                                </div>
                                            </div>
                                        )}

                                    </div>

                                    {/* QUICK PATIENT SWITCHER */}
                                    {isDoctor && (
                                        <div className={`${t.cardBg} border ${t.borderColor} rounded-2xl p-5 shadow-lg flex-1 flex flex-col transition-colors duration-500`}>
                                            <h4 className={`text-xs ${t.subText} uppercase font-bold mb-3 flex items-center gap-2`}><UserCheck size={14}/> Quick Patient Switch</h4>
                                            <div className="relative mb-3">
                                                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${t.subText}`} size={14} />
                                                <input type="text" placeholder="Search ID or Name..." value={quickSearchTerm} onChange={(e) => setQuickSearchTerm(e.target.value)} className={`w-full ${t.innerBg} border ${t.borderColor} ${t.mainText} pl-9 pr-3 py-2 rounded-lg text-xs outline-none focus:${t.borderPrimary}`} disabled={sessionActive} />
                                            </div>
                                            <div className="flex-1 overflow-y-auto max-h-[120px] space-y-2 pr-1 scrollbar-thin scrollbar-thumb-slate-300">
                                                {uniquePatientsList.map(p => (
                                                    <div key={p.id} className={`flex justify-between items-center p-2 rounded-lg border ${patient.id === p.id ? `${t.bgPrimary}/10 ${t.borderPrimary}/50` : `${t.innerBg} ${t.borderColor}`}`}>
                                                        <div className="flex flex-col">
                                                            <span className={`text-xs font-bold ${t.mainText}`}>{p.name}</span>
                                                            <span className={`text-[9px] ${t.subText}`}>{p.id}</span>
                                                        </div>
                                                        {patient.id !== p.id && (
                                                            <button disabled={sessionActive} onClick={() => activatePatientForRecording(p.id)} className={`px-2 py-1 ${t.cardBg} border ${t.borderColor} hover:${t.bgPrimary} hover:text-white text-xs rounded transition-colors ${t.subText}`}>Load</button>
                                                        )}
                                                        {patient.id === p.id && <span className={`text-[9px] ${t.textPrimary} font-bold mr-1`}>ACTIVE</span>}
                                                    </div>
                                                ))}
                                                {uniquePatientsList.length === 0 && <p className={`text-xs ${t.subText} text-center mt-4`}>No matching patients found.</p>}
                                            </div>
                                        </div>
                                    )}

                                    {isDoctor && (
                                        <div className={`${t.cardBg} border ${t.borderColor} rounded-2xl p-5 shadow-lg transition-colors duration-500`}>
                                            <h4 className={`text-xs ${t.subText} uppercase font-bold mb-3`}>IoT Overrides (Room)</h4>
                                            <div className="grid grid-cols-3 gap-2">
                                                <button onClick={() => toggleControl('alarm', alarmActive)} disabled={!sessionActive && autoSaveCountdown === null} className={`p-3 rounded-lg flex flex-col items-center gap-2 border transition-all ${!sessionActive && autoSaveCountdown === null ? `opacity-50 cursor-not-allowed ${t.innerBg} ${t.borderColor}` : alarmActive ? 'bg-red-500/10 border-red-500 text-red-500 shadow-[0_0_10px_rgba(239,68,68,0.2)]' : `${t.innerBg} ${t.borderColor} hover:border-red-500/50 hover:text-red-500`} ${t.subText}`}><Bell size={20}/><span className="text-[10px]">ALARM</span></button>
                                                <button onClick={() => toggleControl('gsm', gsmActive)} disabled={!sessionActive && autoSaveCountdown === null} className={`p-3 rounded-lg flex flex-col items-center gap-2 border transition-all ${!sessionActive && autoSaveCountdown === null ? `opacity-50 cursor-not-allowed ${t.innerBg} ${t.borderColor}` : gsmActive ? 'bg-blue-500/10 border-blue-500 text-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.2)]' : `${t.innerBg} ${t.borderColor} hover:border-blue-500/50 hover:text-blue-500`} ${t.subText}`}><MapPin size={20}/><span className="text-[10px] text-center leading-tight">GPS +<br/>CALL</span></button>
                                                <button onClick={() => toggleControl('light', lightActive)} disabled={!sessionActive && autoSaveCountdown === null} className={`p-3 rounded-lg flex flex-col items-center gap-2 border transition-all ${!sessionActive && autoSaveCountdown === null ? `opacity-50 cursor-not-allowed ${t.innerBg} ${t.borderColor}` : lightActive ? 'bg-yellow-500/10 border-yellow-500 text-yellow-600 shadow-[0_0_10px_rgba(234,179,8,0.2)]' : `${t.innerBg} ${t.borderColor} hover:border-yellow-500/50 hover:text-yellow-600`} ${t.subText}`}><Lightbulb size={20}/><span className="text-[10px]">LIGHT</span></button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* CENTER PANEL: GRAPH */}
                                <div className="col-span-12 lg:col-span-6 flex flex-col gap-4">
                                    <div className={`${t.cardBg} border ${t.borderColor} rounded-2xl p-5 flex flex-col h-[300px] shadow-lg relative overflow-hidden transition-colors duration-500`}>
                                        
                                        <div className="flex justify-between items-start mb-4">
                                            <div>
                                                <h3 className={`font-bold flex items-center gap-2 ${t.mainText}`}><Activity className={sessionActive ? `${t.textPrimary} animate-pulse` : t.subText}/> Live RF Waveform</h3>
                                            </div>
                                            {activeChunk && (
                                                <span className={`px-3 py-1 rounded border text-xs font-bold ${activeChunk.prediction.includes('Normal') ? 'text-green-500 border-green-500/30' : 'text-red-500 border-red-500/30'}`}>
                                                    Window {currentSessionChunks.length}: {activeChunk.prediction}
                                                </span>
                                            )}
                                        </div>

                                        <div className={`flex-1 ${t.innerBg} rounded-xl border ${t.borderColor} p-4 relative transition-colors duration-500`}>
                                            {!sessionActive && !activeChunk && (
                                                <div className={`absolute inset-0 flex items-center justify-center ${t.subText} italic text-sm`}>Awaiting data stream...</div>
                                            )}
                                            {activeChunk && (
                                                <ResponsiveContainer width="100%" height="100%">
                                                    <LineChart data={activeChunk.formattedWave} margin={{ bottom: 10, left: -20 }}>
                                                        <CartesianGrid strokeDasharray="3 3" stroke={t.chartGrid} />
                                                        <XAxis dataKey="time" stroke={t.chartText} tick={{fill: t.chartText, fontSize: 10}} label={{ value: 'Time (s)', position: 'insideBottom', offset: -5, fill: t.chartText, fontSize: 10 }} />
                                                        <YAxis domain={['auto', 'auto']} stroke={t.chartText} tick={{fill: t.chartText, fontSize: 10}} label={{ value: 'Amp', angle: -90, position: 'insideLeft', fill: t.chartText, fontSize: 10 }} />
                                                        <Line type="monotone" dataKey="value" stroke={getGraphColor(activeChunk.prediction)} strokeWidth={3} dot={false} isAnimationActive={false} />
                                                    </LineChart>
                                                </ResponsiveContainer>
                                            )}
                                        </div>
                                    </div>

                                    {/* FIX 2: CSS HEIGHT BUG FIXED. Container expands safely. */}
                                    <div className={`${t.cardBg} border ${t.borderColor} rounded-2xl p-4 flex-1 shadow-lg transition-colors duration-500 flex flex-col`}>
                                        <h4 className={`text-xs ${t.subText} font-bold uppercase mb-3 border-b ${t.borderColor} pb-2`}>Session Windows Log ({currentSessionChunks.length})</h4>
                                        <div className="flex-1 overflow-y-auto max-h-[160px] scrollbar-thin scrollbar-thumb-slate-300 pr-2">
                                            {/* FIX 2: Vertically scrolling grid (4 items per row on desktop) */}
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pb-2">
                                                {currentSessionChunks.map((chunk, i) => (
                                                    <div key={i} className={`min-w-[120px] ${t.innerBg} border ${chunk.isTriggerPoint ? 'border-red-500 border-2 shadow-[0_0_10px_rgba(239,68,68,0.3)]' : t.borderColor} p-3 rounded-xl flex flex-col relative transition-all`}>
                                                        {chunk.isTriggerPoint && <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[8px] font-black px-2 py-0.5 rounded-full z-10 shadow-md">AI ACTION</span>}
                                                        
                                                        <span className={`${t.subText} text-[10px] mb-1`}>Window {i+1} • {chunk.time}</span>
                                                        <span className={`text-xs font-bold ${chunk.prediction.includes('Normal') ? 'text-green-500' : 'text-red-500'}`}>{chunk.prediction}</span>
                                                    </div>
                                                ))}
                                                {currentSessionChunks.length === 0 && <span className={`${t.subText} text-xs italic col-span-full`}>No windows recorded yet.</span>}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* RIGHT PANEL: AI & GAUGE */}
                                <div className="col-span-12 lg:col-span-3 flex flex-col gap-4">
                                    <BpmGauge bpm={currentBpm} isCalculating={sessionActive} t={t} />

                                    <div className={`${t.cardBg} border ${t.borderColor} rounded-2xl p-5 flex-1 relative overflow-hidden min-h-[200px] shadow-lg flex flex-col transition-colors duration-500`}>
                                        <div className={`absolute top-0 left-0 w-1 h-full ${isDoctor ? t.bgPrimary : t.bgSecondary}`}></div>
                                        <h3 className={`font-bold ${t.mainText} flex items-center gap-2 mb-3 text-sm border-b ${t.borderColor} pb-2`}><BrainCircuit size={16}/> Active AI Diagnostics</h3>
                                        <div className="flex-1 overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-slate-300">
                                            <div className={`text-sm ${t.mainText} leading-relaxed font-medium`}>
                                                {isAiLoading ? (
                                                    <span className={`flex items-center gap-2 animate-pulse ${t.textPrimary}`}><Loader2 className="animate-spin" size={14}/> Processing neural model...</span>
                                                ) : (
                                                    <p>
                                                        {aiAcuteSummary.startsWith('[') ? (
                                                            <>
                                                                <span className={`block mb-1 text-xs font-black tracking-widest ${aiAcuteSummary.includes('CRITICAL') || aiAcuteSummary.includes('HIGH') ? 'text-red-500' : aiAcuteSummary.includes('MODERATE') ? 'text-yellow-500' : 'text-green-500'}`}>
                                                                    {aiAcuteSummary.split(']')[0] + ']'}
                                                                </span>
                                                                <span className="text-sm">{aiAcuteSummary.split(']')[1]}</span>
                                                            </>
                                                        ) : (
                                                            aiAcuteSummary
                                                        )}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* SECTION: LONGITUDINAL HISTORY & ANALYSIS */}
                        <div className="mt-8 mb-10">
                            <h2 className={`text-sm font-black ${t.subText} uppercase tracking-[0.2em] mb-4 flex items-center gap-2`}><TrendingUp size={16}/> Longitudinal History & Analysis</h2>
                            <div className="grid grid-cols-12 gap-4">
                                
                                {/* HISTORICAL CONTEXT AI BOX */}
                                <div className={`col-span-12 lg:col-span-4 ${t.cardBg} border ${t.borderColor} rounded-2xl p-5 shadow-lg flex flex-col max-h-[350px] transition-colors duration-500`}>
                                    <h3 className={`font-bold ${t.mainText} flex items-center gap-2 mb-2 border-b ${t.borderColor} pb-2`}><BookOpen size={18}/> Historical Context</h3>
                                    <p className={`${t.textPrimary} text-xs mb-3 font-mono`}>Viewing AI for: {viewingPatientData.name}</p>
                                    
                                    <div className="flex-1 overflow-y-auto pr-3 scrollbar-thin scrollbar-thumb-slate-300">
                                        <p className={`text-base ${t.mainText} leading-relaxed font-medium`}>
                                            {aiDeepAnalysis ? `"${aiDeepAnalysis}"` : "Accumulating historical data for longitudinal AI analysis..."}
                                        </p>
                                    </div>
                                </div>

                                {/* TREND GRAPH */}
                                <div className={`col-span-12 lg:col-span-8 ${t.cardBg} border ${t.borderColor} rounded-2xl p-5 flex flex-col shadow-lg min-h-[300px] max-h-[350px] transition-colors duration-500`}>
                                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4 md:gap-0">
                                        <div>
                                            <h3 className={`font-bold text-sm ${t.mainText}`}>Longitudinal Severity Trend</h3>
                                            <p className={`text-[10px] ${t.subText} mt-1`}>Viewing Graph for: {viewingPatientData.name} ({viewingPatientId})</p>
                                        </div>
                                        <div className={`flex flex-wrap items-center ${t.innerBg} rounded-lg p-1 border ${t.borderColor}`}>
                                            <button onClick={() => setGraphFilter('all')} className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${graphFilter === 'all' ? (isDoctor ? `${t.bgPrimary} text-white` : `${t.bgSecondary} text-white`) : `${t.subText} hover:${t.mainText}`}`}>All</button>
                                            <button onClick={() => setGraphFilter('today')} className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${graphFilter === 'today' ? (isDoctor ? `${t.bgPrimary} text-white` : `${t.bgSecondary} text-white`) : `${t.subText} hover:${t.mainText}`}`}>Today</button>
                                            <button onClick={() => setGraphFilter('week')} className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${graphFilter === 'week' ? (isDoctor ? `${t.bgPrimary} text-white` : `${t.bgSecondary} text-white`) : `${t.subText} hover:${t.mainText}`}`}>7 Days</button>
                                            <select 
                                                value={['all','today','week'].includes(graphFilter) ? "" : graphFilter}
                                                onChange={(e) => { if(e.target.value) setGraphFilter(e.target.value); }}
                                                className={`${t.innerBg} ${t.mainText} border-l ${t.borderColor} text-xs px-2 py-1 ml-2 outline-none cursor-pointer`}
                                            >
                                                <option value="" disabled className="bg-gray-900 text-white">Specific Day</option>
                                                <option value="Monday" className="bg-gray-900 text-white">Monday</option>
                                                <option value="Tuesday" className="bg-gray-900 text-white">Tuesday</option>
                                                <option value="Wednesday" className="bg-gray-900 text-white">Wednesday</option>
                                                <option value="Thursday" className="bg-gray-900 text-white">Thursday</option>
                                                <option value="Friday" className="bg-gray-900 text-white">Friday</option>
                                                <option value="Saturday" className="bg-gray-900 text-white">Saturday</option>
                                                <option value="Sunday" className="bg-gray-900 text-white">Sunday</option>
                                            </select>
                                        </div>
                                    </div>
                                    
                                    <div className="flex-1 w-full relative">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <LineChart data={getFilteredTrendData(viewingPatientId)} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                                                <CartesianGrid strokeDasharray="3 3" stroke={t.chartGrid} />
                                                <XAxis dataKey="time" type="category" stroke={t.chartText} tick={{fill: t.chartText, fontSize: 10}} />
                                                <YAxis stroke={t.chartText} domain={[-2, 3]} ticks={[-2, -1, 0, 1, 2, 3]} 
                                                    tickFormatter={(val) => {
                                                        const labels = { "-2": "Biot", "-1": "Slow Bradypnea", "0": "Normal Eupnea", "1": "Sighing", "2": "Fast Tachypnea", "3": "Kussmaul" };
                                                        return labels[val] || "";
                                                    }} tick={{fill: t.chartText, fontSize: 10}} width={80}
                                                />
                                                <Tooltip 
                                                    cursor={{ stroke: t.chartText, strokeWidth: 1, strokeDasharray: '3 3' }}
                                                    contentStyle={{ backgroundColor: t.tooltipBg, border: `1px solid ${t.tooltipBorder}`, borderRadius: '8px', color: t.tooltipText }} 
                                                    labelStyle={{ color: t.subText, marginBottom: '5px' }}
                                                    formatter={(value, name, props) => [props.payload.label, "Classification"]}
                                                />
                                                <Line type="stepAfter" dataKey="severity" stroke={isDoctor ? (isClinicalTheme ? "#2563eb" : "#00f0ff") : (isClinicalTheme ? "#0d9488" : "#ff007f")} strokeWidth={3} activeDot={{ r: 8, fill: isClinicalTheme ? '#2563eb' : '#fff' }} dot={renderCustomDot} isAnimationActive={false} />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {/* DEDICATED DATABASE TAB */}
                {activeTab === 'database' && isDoctor && (
                    <div className="mt-4 mb-10">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h2 className={`text-2xl font-black ${t.mainText} flex items-center gap-3`}><Users className={t.textPrimary}/> Master Patient Database</h2>
                                <p className={`${t.subText} mt-1 text-sm md:text-base`}>Review comprehensive session logs, filter history, and switch active telemetry contexts.</p>
                            </div>
                        </div>

                        <div className={`${t.cardBg} border ${t.borderColor} rounded-2xl overflow-hidden shadow-lg transition-colors duration-500`}>
                            <div className={`${t.innerBg} p-4 md:p-5 border-b ${t.borderColor} flex flex-col md:flex-row justify-between items-start md:items-center gap-4 md:gap-0`}>
                                <h3 className={`text-xl font-bold ${t.mainText} flex items-center gap-2`}>Patient Records</h3>
                                <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
                                    
                                    <button 
                                        onClick={() => setDbShowOnlyAI(!dbShowOnlyAI)} 
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-bold transition-all ${dbShowOnlyAI ? 'bg-red-500 text-white border-red-500 shadow-[0_0_10px_rgba(239,68,68,0.4)]' : `${t.cardBg} ${t.subText} border-red-500/30 hover:border-red-500 hover:text-red-500`}`}
                                    >
                                        <BrainCircuit size={16} /> 
                                        {dbShowOnlyAI ? 'Clear AI Filter' : 'Show AI Events'}
                                    </button>

                                    <div className={`flex items-center ${t.cardBg} border ${t.borderColor} rounded-lg overflow-hidden flex-1 md:flex-none`}>
                                        <div className={`px-3 ${t.subText} ${t.innerBg} border-r ${t.borderColor} py-2`}><Filter size={16} /></div>
                                        <select value={dbGlobalDateFilter} onChange={(e) => setDbGlobalDateFilter(e.target.value)} className={`bg-transparent ${t.mainText} text-sm px-3 py-2 outline-none cursor-pointer w-full`}>
                                            <option value="all" className="bg-gray-900 text-white">All Dates</option>
                                            <option value="today" className="bg-gray-900 text-white">Today Only</option>
                                            <option value="week" className="bg-gray-900 text-white">Past 7 Days</option>
                                        </select>
                                    </div>
                                    <button onClick={clearHistory} className="p-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"><Trash2 size={16} /></button>
                                    <div className="relative w-full md:w-auto mt-2 md:mt-0">
                                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${t.subText}`} size={16} />
                                        <input type="text" placeholder="Search Name or ID..." value={dbSearchTerm} onChange={(e) => setDbSearchTerm(e.target.value)} className={`${t.cardBg} border ${t.borderColor} ${t.mainText} pl-10 pr-4 py-2 rounded-lg text-sm outline-none focus:${t.borderPrimary} w-full md:w-64 transition-colors`} />
                                    </div>
                                </div>
                            </div>
                            
                            <div className="overflow-x-auto w-full h-[60vh]">
                                <table className="w-full text-left text-sm relative whitespace-nowrap md:whitespace-normal">
                                    <thead className={`${t.innerBg} ${t.subText} uppercase font-bold sticky top-0 z-20 shadow-sm`}>
                                        <tr>
                                            <th className={`p-4 w-1/3 border-b ${t.borderColor}`}>Patient Details</th>
                                            <th className={`p-4 w-1/3 border-b ${t.borderColor}`}>Latest / Aggregate Result</th>
                                            <th className={`p-4 text-right border-b ${t.borderColor}`}>Actions Taken / Chunks</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`divide-y ${t.borderColor}`}>
                                        {Object.keys(groupedDbHistory).length === 0 && (
                                            <tr><td colSpan="3" className={`p-10 text-center ${t.subText}`}>No records found matching your filters.</td></tr>
                                        )}
                                        
                                        {Object.keys(groupedDbHistory).map(patientKey => {
                                            const latestLog = groupedDbHistory[patientKey][0];
                                            const isExpanded = expandedGroups[patientKey] || false;
                                            const isActivePatient = patient.id === patientKey;

                                            const pFilter = dbPatientFilters[patientKey] || { type: 'all', sort: 'desc' };
                                            let logsToShow = [...groupedDbHistory[patientKey]];
                                            
                                            if (pFilter.type === 'abnormal') logsToShow = logsToShow.filter(l => !l.classification.includes('Normal'));
                                            if (pFilter.sort === 'asc') logsToShow.reverse(); 

                                            return (
                                                <React.Fragment key={patientKey}>
                                                    <tr className={`hover:${t.innerBg} cursor-pointer transition-colors`} onClick={() => toggleGroupExpand(patientKey)}>
                                                        <td className={`p-4 font-bold ${t.mainText} text-base flex items-center gap-3`}>
                                                            {isExpanded ? <ChevronUp size={16} className={t.textPrimary}/> : <ChevronDown size={16} className={t.textPrimary}/>}
                                                            <User size={18} className={t.textPrimary}/> 
                                                            <div className="flex flex-col">
                                                                <span className="flex items-center gap-2">
                                                                    {latestLog.name} 
                                                                    {isActivePatient && <span className={`${t.bgPrimary}/10 ${t.textPrimary} text-[9px] px-2 py-0.5 rounded-full border ${t.borderPrimary}`}>ACTIVE</span>}
                                                                </span>
                                                                <span className={`text-xs ${t.subText} font-mono`}>{patientKey}</span>
                                                            </div>
                                                            <span className={`ml-2 text-xs font-normal ${t.subText} ${t.innerBg} px-2 py-1 rounded-full border ${t.borderColor}`}>{groupedDbHistory[patientKey].length} Records</span>
                                                        </td>
                                                        <td className="p-4">
                                                            <span className={`px-3 py-1 rounded-full text-xs font-bold ${latestLog.classification.includes('Normal') || latestLog.classification.includes('Eupnea') ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'}`}>
                                                                {latestLog.classification}
                                                            </span>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            {isExpanded && !isActivePatient && (
                                                                <button onClick={(e) => { e.stopPropagation(); activatePatientForRecording(patientKey); }} className={`px-4 py-2 ${t.bgPrimary}/10 ${t.textPrimary} border ${t.borderPrimary} rounded-lg text-xs font-bold hover:${t.bgPrimary} hover:text-white transition shadow-sm flex items-center gap-2 ml-auto`}>
                                                                    <Activity size={14} /> Send to Dashboard
                                                                </button>
                                                            )}
                                                            {!isExpanded && <span className={`${t.subText} text-xs italic`}>Click to expand</span>}
                                                        </td>
                                                    </tr>

                                                    {isExpanded && (
                                                        <tr className={`${t.innerBg} border-l-4 ${t.borderPrimary} border-b ${t.borderColor}`}>
                                                            <td colSpan="3" className="p-2 pl-4 md:pl-12">
                                                                <div className="flex flex-wrap gap-4 items-center">
                                                                    <span className={`text-[10px] ${t.subText} uppercase font-bold flex items-center gap-1`}><Filter size={12}/> Filter Records:</span>
                                                                    <select value={pFilter.type} onChange={(e) => updatePatientFilter(patientKey, 'type', e.target.value)} className={`${t.cardBg} ${t.mainText} border ${t.borderColor} text-xs rounded px-2 py-1 outline-none cursor-pointer`}>
                                                                        <option value="all" className="bg-gray-900 text-white">All Conditions</option>
                                                                        <option value="abnormal" className="bg-gray-900 text-white">Abnormal Findings Only</option>
                                                                    </select>
                                                                    <select value={pFilter.sort} onChange={(e) => updatePatientFilter(patientKey, 'sort', e.target.value)} className={`${t.cardBg} ${t.mainText} border ${t.borderColor} text-xs rounded px-2 py-1 outline-none cursor-pointer`}>
                                                                        <option value="desc" className="bg-gray-900 text-white">Newest First</option>
                                                                        <option value="asc" className="bg-gray-900 text-white">Oldest First</option>
                                                                    </select>
                                                                    {logsToShow.length === 0 && <span className="text-xs text-red-500 italic ml-4">No records match this filter.</span>}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}

                                                    {isExpanded && logsToShow.map((log) => {
                                                        const isAiEvent = log.action && (log.action.includes('Agentic AI') || log.action.includes('Continuous Anomaly'));
                                                        return (
                                                        <tr key={log.timestamp} className={`${t.appBg} border-l-4 ${isAiEvent ? 'border-red-500/50' : t.borderPrimary + '/30'} hover:${t.innerBg} transition-colors`}>
                                                            <td className="p-4 pl-4 md:pl-12 flex flex-col gap-2">
                                                                <span className={`font-bold text-xs ${isAiEvent ? 'text-red-400' : t.mainText}`}>{getValidDate(log.timestamp).toLocaleDateString()} at {log.time}</span>
                                                                {log.bpm && <span className={`text-xs ${t.subText}`}>Recorded BPM: {log.bpm}</span>}
                                                                {log.chunkBreakdown && (
                                                                    <div className="flex flex-wrap gap-2 mt-1 max-w-md">
                                                                        {log.chunkBreakdown.map((chunk, idx) => (
                                                                            <span key={idx} className={`text-xs font-bold px-2.5 py-1 rounded-md border ${chunk.includes('Normal') ? 'text-green-500 border-green-500/20 bg-green-500/5' : 'text-red-500 border-red-500/20 bg-red-500/5'}`}>C{idx+1}: {chunk}</span>
                                                                        ))}
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className={`p-4 ${t.mainText} text-sm font-bold`}>{log.classification}</td>
                                                            <td className={`p-4 text-right text-sm font-semibold ${isAiEvent ? 'text-red-500 animate-pulse' : 'text-yellow-600'}`}>{log.action}</td>
                                                        </tr>
                                                    )})}
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {/* MODULAR TABS */}
                {activeTab === 'devices' && <DevicesTab hardwareMetrics={hardwareMetrics} alarmActive={alarmActive} gsmActive={gsmActive} lightActive={lightActive} t={t} />}
                {activeTab === 'docs' && <DocsTab t={t} />}

            </div>
            <AnimatePresence>
                    {showProfileModal && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[300] flex items-center justify-center bg-black/90 backdrop-blur-md p-4">
                            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} className={`${t.cardBg} border ${t.borderColor} p-6 md:p-8 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto ${t.shadow} relative transition-colors duration-500`}>
                                <div className={`flex justify-between items-center mb-6 border-b ${t.borderColor} pb-4`}>
                                    <h2 className={`text-xl md:text-2xl font-bold ${t.mainText}`}>Register / Edit Patient Profile</h2>
                                    <button onClick={() => setShowProfileModal(false)} className={`${t.subText} hover:${t.mainText} ${t.innerBg} p-2 rounded-full`}><X size={20} /></button>
                                </div>
                                <form onSubmit={handleProfileUpdate} className="space-y-5">
                                    <p className={`text-xs ${t.subText} mb-4`}>Leave ID blank to generate a new one, or enter an existing ID to load a patient record.</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                        <div className="md:col-span-2"><label className={`text-xs ${t.textPrimary} font-bold uppercase ml-1`}>Patient ID</label><input type="text" value={editPatientForm.id || ''} onChange={(e) => setEditPatientForm({...editPatientForm, id: e.target.value})} placeholder="e.g. P-F-1234 (Leave blank for new)" className={`mt-1 w-full ${t.innerBg} border ${t.borderPrimary} p-3 rounded-xl ${t.mainText} outline-none placeholder-gray-500 uppercase transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Patient Name</label><input type="text" value={editPatientForm.name} onChange={(e) => setEditPatientForm({...editPatientForm, name: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} required/></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Father's Name</label><input type="text" value={editPatientForm.fatherName} onChange={(e) => setEditPatientForm({...editPatientForm, fatherName: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} required/></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Phone Number</label><input type="tel" value={editPatientForm.phone} onChange={(e) => setEditPatientForm({...editPatientForm, phone: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Email</label><input type="email" value={editPatientForm.email} onChange={(e) => setEditPatientForm({...editPatientForm, email: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div className="md:col-span-2"><label className={`text-xs ${t.subText} uppercase ml-1`}>Full Address</label><input type="text" value={editPatientForm.address} onChange={(e) => setEditPatientForm({...editPatientForm, address: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>City</label><input type="text" value={editPatientForm.city} onChange={(e) => setEditPatientForm({...editPatientForm, city: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                        <div><label className={`text-xs ${t.subText} uppercase ml-1`}>Country</label><input type="text" value={editPatientForm.country} onChange={(e) => setEditPatientForm({...editPatientForm, country: e.target.value})} className={`mt-1 w-full ${t.innerBg} border ${t.borderColor} p-3 rounded-xl ${t.mainText} outline-none focus:${t.borderPrimary} transition-colors`} /></div>
                                    </div>
                                    <button type="submit" className={`w-full ${t.bgPrimary} text-white font-bold py-4 rounded-xl mt-6 hover:opacity-90 transition-all ${t.shadow} flex justify-center items-center gap-2`}><Save size={20}/> Load / Save Profile</button>
                                </form>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            
        </div>
    );
};

export default FYP;